import React, { useState } from "react";
import { Button } from "@components/ui/button";
import { CirclePlus, CircleMinus, PlusCircle } from "lucide-react";
import PageLoader from "@components/loaders/PageLoader";
import {
  CommonApiRequestPayload,
  fetchConfigPage,
  fetchLogicPageInfo,
  sendRequest,
} from "./createPluginApi";
import { toast } from "@components/ui/use-toast";
import { DefinedVariable } from "./allData";
import {
  useMeshPluginContext,
  MeshPluginActions,
} from "@services/providers/MeshPlugin";
import PaginationBlock from "./PaginationBlock";
import FieldMappingDialog from "./FieldMappingDialog";

import { useQuery } from "react-query";
import ResponseActionsDialog from "./ResponseActionsDialog";
import QueryBuilderResponse from "./QueryBuilderResponse";
import Authorization from "./Authorization";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import QueryBuilderBody from "./QueryBuilderBody";
import {
  interpolateDefinedVariablesOnly,
  mapAuthTypeToBackend,
  normalizeResponseData,
  validateBodyContent,
  validateLoopingFromUrl,
  validateVariableUsageInJSON,
} from "./logicUtils";
import QueryBuilderHeader from "./QueryBuilderHeaders";
import { useCustomToast } from "@components/common/CustomToast";

export type Header = { key: string; value: string };

type QueryBuilderProps = {
  previousResponse?: any;
  setSelectedApiData?: (data: any) => void;
  requestId?: number | undefined;
  setDefinedVariables?: (variables: any) => void;
  definedVariables?: DefinedVariable[];
  finalRequestData: {
    request: any[];
    variables: any[];
    responses: Record<number, any>;
  };
  setFinalRequestData: React.Dispatch<
    React.SetStateAction<{
      request: any[];
      variables: any[];
      responses: Record<number, any>;
    }>
  >;
  setIsResponse?: (open: boolean) => void;
  setResponse?: (val: any) => void;
  setBlockResponse: (id: number, response: any) => void;
  blockId: number;
  selectedLogicBlockId?: number | null;
  pluginId?: string | null;
  setLogMappedFields?: React.Dispatch<React.SetStateAction<any[]>>;
  setMappingSaveData?: React.Dispatch<React.SetStateAction<any[]>>;
  mappingSaveData?: any;
  updateRequest?: (
    requestId: number | undefined,
    updatedFields: Partial<CommonApiRequestPayload>,
  ) => void;
};
type PaginationParamRow = {
  paginationEnable: boolean;
  categoryTypeKey: string;
  categoryTypeValue: string;
  paginationTypeKey: string;
  paginationTypeValue: string;
  paramKey?: string;
  paramValue?: string;
  categoryTypeCustom?: string;
  paginationTypeCustom?: string;
  limitKey: string;
  limitValue: string;
  limitCustomKey: string;
  cursorKey: string;
  cursorValue: string;
  cursorSourcePath: string;
  tokenKey: string;
  tokenValue: string;
  tokenSourcePath: string;
  nextUrlKey: string;
  nextUrlValue: string;
  nextUrlSourcePath: string;
};

const httpMethods = ["GET", "POST", "PUT", "DELETE", "PATCH"];

const QueryBuilder: React.FC<QueryBuilderProps> = ({
  // setSelectedApiData,
  requestId,
  setDefinedVariables,
  definedVariables,
  finalRequestData,
  setFinalRequestData,
  setIsResponse,
  setResponse,
  setBlockResponse,
  blockId,
  selectedLogicBlockId,
  pluginId,
  setLogMappedFields,
  setMappingSaveData,
  mappingSaveData,
  updateRequest,
}) => {
  const {
    selectedConfigData,
    selectedLogicName,
    openMapfieldsDialog,
    dispatch,
    hasTempConfigPage,
  } = useMeshPluginContext();
  const [method, setMethod] = useState("GET");
  const [targetUrl, setTargetUrl] = useState("");
  const [headers, setHeaders] = useState<Header[]>([]);
  const [body, setBody] = useState("");
  const [bodyType, setBodyType] = useState("none");
  const [rawBodyType, setRawBodyType] = useState("text");
  const [formData, setFormData] = useState<
    Array<{ key: string; value: string; enabled: boolean }>
  >([{ key: "", value: "", enabled: true }]);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [bodyValidationError, setBodyValidationError] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authHeader, setAuthHeader] = useState("authorization");
  const [authToken, setAuthToken] = useState("");
  const [customAuthKey, setCustomAuthKey] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [inputName, setInputName] = useState("");
  const [isCommonVar, setIsCommonVar] = useState(false);
  const [isLogMapping, setIsLogMapping] = useState(false);
  const [selectedValue, setSelectedValue] = useState<any>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [showParams, setShowParams] = useState(false);
  const [showQueryParams, setShowQueryParams] = useState(false);
  const [queryParams, setQueryParams] = useState<
    Array<{ enabled: boolean; key: string; value: string }>
  >([{ enabled: true, key: "", value: "" }]);
  const [paginationFormData, setPaginationFormData] = useState<
    PaginationParamRow[]
  >([
    {
      paginationEnable: true,
      categoryTypeKey: "",
      categoryTypeValue: "",
      paginationTypeKey: "",
      paginationTypeValue: "",
      paramKey: "",
      paramValue: "",
      categoryTypeCustom: "",
      paginationTypeCustom: "",
      limitKey: "",
      limitValue: "",
      limitCustomKey: "",
      cursorKey: "",
      cursorValue: "",
      cursorSourcePath: "",
      tokenKey: "",
      tokenValue: "",
      tokenSourcePath: "",
      nextUrlKey: "",
      nextUrlValue: "",
      nextUrlSourcePath: "",
    },
  ]);
  const [sslVerify, setSslVerify] = useState(true);
  type MappingPath = { value: any; path: string | null } | null;
  const [selectedMappingPath, setSelectedMappingPath] =
    useState<MappingPath>(null);
  const [config, setConfig] = useState<any>({});
  const [data, setData] = useState<any>({});
  const [isAuthVar, setIsAuthVar] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>("none");
  const [paginationType, setPaginationType] = React.useState<string>("none");
  const [filterationType, setFilterationType] = React.useState<string>("none");
  const [startKey, setStartKey] = React.useState<string>("");
  const [endKey, setEndKey] = React.useState<string>("");
  const [customDateFormat, setCustomDateFormat] = React.useState<string>("");
  const [logCollectionPeriod, setLogCollectionPeriod] =
    React.useState<string>("none");

  const mapRefInput = React.useRef<HTMLInputElement>(null);
  const isUpdatingFromUrl = React.useRef<boolean>(false);
  const isUpdatingFromPagination = React.useRef<boolean>(false);
  const isTyping = React.useRef<boolean>(false);
  const isPaginationLoaded = React.useRef<boolean>(false);
  const lastLoadedRequestId = React.useRef<number | undefined>(undefined);
  const hasUserToggledParams = React.useRef<boolean>(false);
  const isTogglingAuth = React.useRef<boolean>(false);
  const hasAutoShownParams = React.useRef<Set<number>>(new Set());
  const lastLoadedFilterationRequestId = React.useRef<number | undefined>(
    undefined,
  );

  const getReservedQueryParamKeys = React.useCallback(() => {
    const reserved = new Set<string>();

    const addKey = (key?: string) => {
      const normalized = (key || "").trim().toLowerCase();
      if (normalized) {
        reserved.add(normalized);
      }
    };

    paginationFormData.forEach((row) => {
      if (!row.paginationEnable) return;

      addKey(
        row.categoryTypeKey === "custom"
          ? row.categoryTypeCustom
          : row.categoryTypeKey,
      );
      addKey(
        row.paginationTypeKey === "custom"
          ? row.paginationTypeCustom
          : row.paginationTypeKey,
      );
      addKey(row.paramKey);
      addKey(row.limitKey === "custom" ? row.limitCustomKey : row.limitKey);
      addKey(row.cursorKey);
      addKey(row.tokenKey);
      addKey(row.nextUrlKey);
    });

    if (filterationType && filterationType !== "none") {
      addKey(startKey);
      addKey(endKey);
    }

    return reserved;
  }, [paginationFormData, filterationType, startKey, endKey]);

  const sanitizeQueryParams = React.useCallback(
    (params: Array<{ enabled: boolean; key: string; value: string }>) => {
      const reserved = getReservedQueryParamKeys();

      return params.filter((param) => {
        const normalizedKey = (param?.key || "").trim().toLowerCase();
        if (!normalizedKey) return true;
        return !reserved.has(normalizedKey);
      });
    },
    [getReservedQueryParamKeys],
  );

  const safeQueryParams = React.useMemo(() => {
    const sanitized = sanitizeQueryParams(queryParams);
    return sanitized.length > 0
      ? sanitized
      : [{ enabled: true, key: "", value: "" }];
  }, [queryParams, sanitizeQueryParams]);

  const { showToast } = useCustomToast();

  const response = finalRequestData?.responses?.[requestId ?? 0] || null;

  // Check if we're in edit mode
  const isEditMode = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("edit") === "1";
  }, []);

  // Fetch config page data
  const { data: configPageData, isLoading: isConfigLoading } = useQuery(
    ["configPage", pluginId, "temp"],
    () => fetchConfigPage(pluginId ?? "", "temp"),
    {
      enabled: !!pluginId && isEditMode && hasTempConfigPage,
    },
  );

  // Fetch logic page data
  const { data: fetchedLogicPageData } = useQuery({
    queryKey: ["fetchLogicPageData", pluginId],
    queryFn: () => fetchLogicPageInfo(pluginId!),
    enabled: false,
  });

  // Extract mappedRequestIds from both backend and local state for the current bundle only
  const backendMappedRequestIds =
    fetchedLogicPageData?.data?.LogicBundles?.flatMap((bundle: any) => {
      // Only check mappings for the current bundle
      if (bundle.bundleId !== selectedLogicBlockId) return [];

      return Array.isArray(bundle.mappings)
        ? bundle.mappings.map((mapping: any) => String(mapping.requestId))
        : [];
    }).filter((id: any) => id && id !== "undefined") || [];

  // Get request IDs from local mappingSaveData for the current bundle only
  const localMappedRequestIds = (
    mappingSaveData?.[selectedLogicBlockId || ""] || []
  )
    .filter((m: any) => m.logType === "parquet")
    .map((m: any) => String(m.requestId))
    .filter((id: any) => id && id !== "undefined");

  // Combine both backend and local mapped request IDs for current bundle
  const mappedRequestIds = [
    ...new Set([...backendMappedRequestIds, ...localMappedRequestIds]),
  ];

  // handle pagination type change
  const handlePaginationTypeChange = React.useCallback((value: string) => {
    setPaginationType(value);
  }, []);

  // Set config when fetched
  const EXCLUDE_DATA_KEYS = ["uid", "isDecript"];

  React.useEffect(() => {
    if (configPageData?.data) {
      const [tabId, items] =
        (Object.entries(configPageData.data)[0] as [string, any[]]) || [];
      const item = items?.[0];
      if (!item) return;

      setConfig({
        tabId,
        uid: item.uid,
        configType: "temp",
      });

      setData(
        Object.fromEntries(
          Object.entries(item).filter(
            ([key]) => !EXCLUDE_DATA_KEYS.includes(key),
          ),
        ),
      );
    }
  }, [configPageData]);

  // Update config and data when selectedConfigData changes
  React.useEffect(() => {
    if (selectedConfigData) {
      const configToUse = Array.isArray(selectedConfigData)
        ? selectedConfigData[0]
        : selectedConfigData;

      if (configToUse) {
        setConfig((prevConfig: any) => ({
          tabId: prevConfig.tabId || "",
          uid: configToUse.uid,
          configType: "temp",
        }));

        setData(
          Object.fromEntries(
            Object.entries(configToUse).filter(
              ([key]) => !EXCLUDE_DATA_KEYS.includes(key),
            ),
          ),
        );
      }
    }
  }, [selectedConfigData]);

  // Initialize form fields if requestId changes
  React.useEffect(() => {
    if (!requestId) return;

    // Skip re-initialization if we're just toggling auth
    if (isTogglingAuth.current) {
      isTogglingAuth.current = false;
      return;
    }

    const existingRequest = finalRequestData?.request?.find(
      (req) => req?.requestId == requestId,
    );

    if (existingRequest) {
      setTargetUrl(existingRequest.url || "");
      setMethod(existingRequest.method || "GET");
      setBody(existingRequest.body || "");
      // Support both camelCase (frontend) and snake_case (backend) formats
      setBodyType(
        existingRequest.bodyType ?? existingRequest.body_type ?? "none",
      );
      setRawBodyType(
        existingRequest.rawBodyType ?? existingRequest.raw_type ?? "text",
      );
      setFormData(
        existingRequest.formData || [{ key: "", value: "", enabled: true }],
      );
      const cleanedHeaders = Array.isArray(existingRequest.headers)
        ? existingRequest.headers.filter((h: any) => h.key || h.value)
        : [];
      setHeaders(cleanedHeaders);
      setSslVerify(
        typeof existingRequest.ssl_verify === "boolean"
          ? existingRequest.ssl_verify
          : true,
      );

      // Restore auth type - map backend format to frontend format
      const savedAuthType = existingRequest.auth?.type || "authorization";
      let frontendAuthType: string;

      // Load query params from params.parameter if available
      // Track if we loaded queryParams successfully
      let queryParamsLoaded = false;

      // PRIORITY 1: Try to parse from URL (preserves template variables like {{action.vdoms}})
      // BUT: Exclude pagination keys - they should only be in pagination section
      const urlToCheck =
        existingRequest.originalUrl || existingRequest.url || "";
      const queryStringMatch = urlToCheck.match(/\?(.+)$/);

      if (queryStringMatch) {
        const queryString = queryStringMatch[1].trim();
        if (queryString) {
          // Collect pagination keys to exclude from queryParams
          const paginationKeys = new Set<string>();

          // Get pagination keys from paginationType and paginationFormData
          if (
            existingRequest.paginationType &&
            existingRequest.paginationType !== "none"
          ) {
            const formData = existingRequest.paginationFormData || [];
            formData.forEach((row: any) => {
              if (!row.paginationEnable) return;

              // Add keys based on pagination type
              if (existingRequest.paginationType === "offset_limit") {
                if (row.categoryTypeKey && row.categoryTypeKey !== "custom") {
                  paginationKeys.add(row.categoryTypeKey);
                }
                if (
                  row.paginationTypeKey &&
                  row.paginationTypeKey !== "custom"
                ) {
                  paginationKeys.add(row.paginationTypeKey);
                }
                if (
                  row.categoryTypeKey === "custom" &&
                  row.categoryTypeCustom
                ) {
                  paginationKeys.add(row.categoryTypeCustom);
                }
                if (
                  row.paginationTypeKey === "custom" &&
                  row.paginationTypeCustom
                ) {
                  paginationKeys.add(row.paginationTypeCustom);
                }
              } else if (
                existingRequest.paginationType === "page_page_size" ||
                existingRequest.paginationType === "page_size"
              ) {
                if (row.categoryTypeKey && row.categoryTypeKey !== "custom") {
                  paginationKeys.add(row.categoryTypeKey);
                }
                if (
                  row.paginationTypeKey &&
                  row.paginationTypeKey !== "custom"
                ) {
                  paginationKeys.add(row.paginationTypeKey);
                }
              } else if (
                ["cursor", "token", "next_url"].includes(
                  existingRequest.paginationType,
                )
              ) {
                if (row.limitKey && row.limitKey !== "custom") {
                  paginationKeys.add(row.limitKey);
                }
                if (row.limitKey === "custom" && row.limitCustomKey) {
                  paginationKeys.add(row.limitCustomKey);
                }
                if (
                  existingRequest.paginationType === "cursor" &&
                  row.cursorKey
                ) {
                  paginationKeys.add(row.cursorKey);
                }
                if (
                  existingRequest.paginationType === "token" &&
                  row.tokenKey
                ) {
                  paginationKeys.add(row.tokenKey);
                }
                if (
                  existingRequest.paginationType === "next_url" &&
                  row.nextUrlKey
                ) {
                  paginationKeys.add(row.nextUrlKey);
                }
              }
            });
          }

          // Parse URL params and exclude pagination keys
          const urlParams: Array<{
            enabled: boolean;
            key: string;
            value: string;
          }> = [];
          const rawParams = queryString.split("&");

          rawParams.forEach((param: any) => {
            if (param && param.includes("=")) {
              const [key, ...valueParts] = param.split("=");
              const value = valueParts.join("="); // Handle values with = in them

              // Don't decode template variables - keep them as-is
              const decodedKey = key.includes("{{")
                ? key
                : decodeURIComponent(key);
              const decodedValue = value.includes("{{")
                ? value
                : decodeURIComponent(value);

              // Skip pagination keys - they belong in pagination section
              if (!paginationKeys.has(decodedKey.trim())) {
                urlParams.push({
                  enabled: true,
                  key: decodedKey.trim(),
                  value: decodedValue.trim(),
                });
              }
            }
          });

          if (urlParams.length > 0) {
            setQueryParams(urlParams);
            setShowParams(true);
            queryParamsLoaded = true;

            setTimeout(() => {
              setFinalRequestData((prev: any) =>
                updateRequestInFinalData({
                  prev,
                  requestId,
                  updates: { queryParams: urlParams },
                }),
              );
            }, 0);
          }
        }
      }

      // PRIORITY 2: Check queryParams field (frontend format)
      if (
        !queryParamsLoaded &&
        existingRequest.queryParams &&
        Array.isArray(existingRequest.queryParams)
      ) {
        // Check if there are any valid params (with actual key or value, ignoring whitespace)
        const validParams = existingRequest.queryParams.filter(
          (p: any) => (p.key && p.key.trim()) || (p.value && p.value.trim()),
        );

        if (validParams.length > 0) {
          setQueryParams(existingRequest.queryParams);
          setShowParams(true);
          queryParamsLoaded = true;
        } else {
          // Has queryParams array but all are empty - hide the section
          queryParamsLoaded = true;
          setShowParams(false);
        }
      }

      // PRIORITY 3: Fall back to params.parameter (backend format - may have hardcoded values)
      if (
        !queryParamsLoaded &&
        existingRequest.params?.type === "params" &&
        existingRequest.params?.parameter &&
        Array.isArray(existingRequest.params.parameter)
      ) {
        const validParams = existingRequest.params.parameter.filter(
          (param: any) => param.key || param.value,
        );

        if (validParams.length > 0) {
          const convertedParams = validParams.map((param: any) => ({
            enabled: true,
            key: param.key || "",
            value: param.value || "",
          }));

          setQueryParams(convertedParams);
          setShowParams(true);
          queryParamsLoaded = true;

          setTimeout(() => {
            setFinalRequestData((prev: any) =>
              updateRequestInFinalData({
                prev,
                requestId,
                updates: { queryParams: convertedParams },
              }),
            );
          }, 0);
        }
      }

      // Only reset if we didn't load any query params
      if (!queryParamsLoaded) {
        // Reset to default if no params at all
        setQueryParams([{ enabled: true, key: "", value: "" }]);
      }

      // Map backend auth type to frontend format
      if (!savedAuthType || savedAuthType === "authorization") {
        frontendAuthType = "authorization";
      } else {
        // Map from backend format to frontend format
        switch (savedAuthType) {
          case "basic_auth":
            frontendAuthType = "basic";
            break;
          case "bearer_token":
            frontendAuthType = "bearer";
            break;
          case "jwt_token":
            frontendAuthType = "jwtBearer";
            break;
          case "api_key":
            frontendAuthType = "api-key";
            break;
          case "authorization":
            frontendAuthType = "authorization";
            break;
          default:
            // If it's already in frontend format, use as-is
            frontendAuthType = savedAuthType;
        }
      }

      setAuthHeader(frontendAuthType);

      // Restore ALL auth values
      const savedUsername = existingRequest.auth?.username || "";
      const savedPassword = existingRequest.auth?.password || "";
      const savedToken = existingRequest.auth?.token || "";

      setUsername(savedUsername);
      setPassword(savedPassword);
      setAuthToken(savedToken);

      // Determine if auth should be shown
      const hasBasicAuth = !!(savedUsername || savedPassword);
      const hasTokenAuth = !!savedToken;

      // Set showAuth based on auth type and values
      let calculatedShowAuth = false;
      if (frontendAuthType === "basic") {
        calculatedShowAuth = hasBasicAuth;
      } else if (
        frontendAuthType !== "none" &&
        frontendAuthType !== "authorization"
      ) {
        // Show auth for bearer, jwtBearer, api-key etc if we have token
        calculatedShowAuth = hasTokenAuth;
      } else if (frontendAuthType === "authorization") {
        // For authorization header, show if token exists
        calculatedShowAuth = hasTokenAuth;
      } else {
        calculatedShowAuth = false;
      }

      setShowAuth(calculatedShowAuth);

      // Override with explicit showAuth if present (only from backend data, not our saved state)
      if (typeof existingRequest.showAuth === "boolean") {
        setShowAuth(existingRequest.showAuth);
      }

      // Transform backend pagination format to frontend format if needed
      if (
        existingRequest.pagination &&
        existingRequest.pagination.type &&
        existingRequest.pagination.type !== "none" &&
        !existingRequest.paginationType
      ) {
        // Transform backend pagination.parameter object to paginationFormData
        const param = existingRequest.pagination.parameter || {};
        const backendType = existingRequest.pagination.type;

        // Map backend type to frontend type
        let frontendType = "";
        if (backendType === "offset_limit_pagination")
          frontendType = "offset_limit";
        else if (backendType === "page_size_pagination")
          frontendType = "page_page_size";
        else if (backendType === "cursor_pagination") frontendType = "cursor";
        else if (backendType === "token_pagination") frontendType = "token";
        else if (backendType === "next_url_pagination")
          frontendType = "next_url";

        if (frontendType && Object.keys(param).length > 0) {
          const row: PaginationParamRow = {
            paginationEnable: true,
            categoryTypeKey: "",
            categoryTypeValue: "",
            paginationTypeKey: "",
            paginationTypeValue: "",
            paramKey: "",
            paramValue: "",
            categoryTypeCustom: "",
            paginationTypeCustom: "",
            limitKey: "",
            limitValue: "",
            limitCustomKey: "",
            cursorKey: "",
            cursorValue: "",
            cursorSourcePath: "",
            tokenKey: "",
            tokenValue: "",
            tokenSourcePath: "",
            nextUrlKey: "",
            nextUrlValue: "",
            nextUrlSourcePath: "",
          };

          if (frontendType === "offset_limit") {
            const offsetKeyStandard = ["offset", "skip", "start"];
            const limitKeyStandard = ["limit", "count", "pageSize", "perPage"];

            const offsetKey = param.offset_key || "";
            const limitKey = param.limit_key || "";

            // Handle offset key
            if (offsetKey && !offsetKeyStandard.includes(offsetKey)) {
              row.categoryTypeKey = "custom";
              row.categoryTypeCustom = offsetKey;
            } else {
              row.categoryTypeKey = offsetKey;
            }
            row.categoryTypeValue = param.offset_value || "";

            // Handle limit key
            if (limitKey && !limitKeyStandard.includes(limitKey)) {
              row.paginationTypeKey = "custom";
              row.paginationTypeCustom = limitKey;
            } else {
              row.paginationTypeKey = limitKey;
            }
            row.paginationTypeValue = param.limit_value || "";
          } else if (frontendType === "page_page_size") {
            const pageKeyStandard = ["page", "p", "pageNumber"];
            const perPageKeyStandard = ["size", "pageSize", "perPage"];

            const pageKey = param.page_key || "";
            const perPageKey = param.per_page_key || "";

            // Handle page key
            if (pageKey && !pageKeyStandard.includes(pageKey)) {
              row.categoryTypeKey = "custom";
              row.categoryTypeCustom = pageKey;
            } else {
              row.categoryTypeKey = pageKey;
            }
            row.categoryTypeValue = param.page_value || "";

            // Handle per page key
            if (perPageKey && !perPageKeyStandard.includes(perPageKey)) {
              row.paginationTypeKey = "custom";
              row.paginationTypeCustom = perPageKey;
            } else {
              row.paginationTypeKey = perPageKey;
            }
            row.paginationTypeValue = param.per_page_value || "";
          } else if (frontendType === "cursor") {
            const limitKeyValue = param.limit_key || "";

            // Handle limit key - only "limit" is standard
            if (limitKeyValue && limitKeyValue !== "limit") {
              row.limitKey = "custom";
              row.limitCustomKey = limitKeyValue;
            } else {
              row.limitKey = limitKeyValue;
            }
            row.limitValue = param.limit_value || "";
            row.cursorKey = param.cursor_key || "";
            row.cursorValue = param.cursor_value || "";
            row.cursorSourcePath = param.cursor_value || "";
          } else if (frontendType === "token") {
            const limitKeyValue = param.limit_key || "";

            // Handle limit key - only "limit" is standard
            if (limitKeyValue && limitKeyValue !== "limit") {
              row.limitKey = "custom";
              row.limitCustomKey = limitKeyValue;
            } else {
              row.limitKey = limitKeyValue;
            }
            row.limitValue = param.limit_value || "";
            row.tokenKey = param.token_key || "";
            row.tokenValue = param.token_value || "";
            row.tokenSourcePath = param.token_value || "";
          } else if (frontendType === "next_url") {
            const limitKeyValue = param.limit_key || "";

            // Handle limit key - only "limit" is standard
            if (limitKeyValue && limitKeyValue !== "limit") {
              row.limitKey = "custom";
              row.limitCustomKey = limitKeyValue;
            } else {
              row.limitKey = limitKeyValue;
            }
            row.limitValue = param.limit_value || "";
            row.nextUrlKey = param.next_url_key || "";
            row.nextUrlValue = "";
            row.nextUrlSourcePath = ""; // Response path not needed for next_url
          }

          // Set local state immediately
          setPaginationType(frontendType);
          setPaginationFormData([row]);

          // Update finalRequestData with transformed pagination data
          setFinalRequestData((prev: any) =>
            updateRequestInFinalData({
              prev,
              requestId,
              updates: {
                paginationType: frontendType,
                paginationFormData: [row],
              },
            }),
          );

          // Reset loaded flag so the pagination loading effect can run again
          lastLoadedRequestId.current = undefined;
        }
      }

      // Pagination and query params loaded separately in their own useEffect
      // Note: queryParams are now loaded and combined with pagination params in the pagination loading useEffect

      // Set response if available
      if (finalRequestData?.responses?.[requestId]) {
        setResponse?.(finalRequestData.responses[requestId]);
        setIsResponse?.(true);
      } else {
        setResponse?.(null);
        setIsResponse?.(false);
      }

      // CODE NEEDED FOR FUTURE USE - filteration restoration
      // if (existingRequest?.filteration && existingRequest?.filteration?.type) {
      //   if (existingRequest?.filteration?.type == "none") {
      //     setFilterationType("none");
      //     return;
      //   }
      //   const filterType =
      //     existingRequest?.filteration?.type === "custom_date_format"
      //       ? "custom"
      //       : existingRequest?.filteration?.type || "none";
      //   setFilterationType(filterType);
      //   setStartKey(existingRequest?.filteration?.start_key || "");
      //   setEndKey(existingRequest?.filteration?.end_key || "");
      //   setCustomDateFormat(
      //     existingRequest?.filteration.custom_date_format || "",
      //   );
      //   setLogCollectionPeriod(
      //     existingRequest?.filteration?.default_cron_start_time || "none",
      //   );
      // } else {
      //   setCustomDateFormat("");
      //   setFilterationType("none");
      //   setStartKey("");
      //   setEndKey("");
      //   setCustomDateFormat("");
      //   setLogCollectionPeriod("none");
      // }
    } else {
      setTargetUrl("");
      setMethod("GET");
      setBody("");
      setBodyType("none");
      setRawBodyType("text");
      setFormData([{ key: "", value: "", enabled: true }]);
      setHeaders([]);
      setAuthHeader("authorization");
      setAuthToken("");
      setUsername("");
      setPassword("");
      setShowAuth(false);
      setSslVerify(true);
      setFilterationType("none");
      setStartKey("");
      setEndKey("");
      setCustomDateFormat("");
      setLogCollectionPeriod("none");
      setPaginationType("none");
      setPaginationFormData([
        {
          paginationEnable: true,
          categoryTypeKey: "",
          categoryTypeValue: "",
          paginationTypeKey: "",
          paginationTypeValue: "",
          paramKey: "",
          paramValue: "",
          categoryTypeCustom: "",
          paginationTypeCustom: "",
          limitKey: "",
          limitValue: "",
          limitCustomKey: "",
          cursorKey: "",
          cursorValue: "",
          cursorSourcePath: "",
          tokenKey: "",
          tokenValue: "",
          tokenSourcePath: "",
          nextUrlKey: "",
          nextUrlValue: "",
          nextUrlSourcePath: "",
        },
      ]);
      setQueryParams([{ enabled: true, key: "", value: "" }]);
      setResponse?.(null);
      setIsResponse?.(false);
      setShowParams(false);
    }
  }, [requestId]);

  // Initialize filteration fields if requestId changes
  React.useEffect(() => {
    if (!requestId) return;

    // Only load once per requestId to prevent glitching
    if (lastLoadedFilterationRequestId.current === requestId) {
      return;
    }

    const existingRequest = finalRequestData?.request?.find(
      (req) => req?.requestId == requestId,
    );

    if (!existingRequest) return;

    // Mark this request as loaded
    lastLoadedFilterationRequestId.current = requestId;

    // Load filteration data - map custom_date_format back to custom for UI
    if (existingRequest?.filteration) {
      if (existingRequest?.filteration?.type == "none") {
        setFilterationType("none");
        return;
      }
      const filterType =
        existingRequest?.filteration?.type === "custom_date_format"
          ? "custom"
          : existingRequest?.filteration?.type || "none";

      setFilterationType(filterType);
      setStartKey(existingRequest?.filteration?.start_key || "");
      setEndKey(existingRequest?.filteration?.end_key || "");
      setCustomDateFormat(
        existingRequest?.filteration?.custom_date_format || "",
      );
      setLogCollectionPeriod(
        existingRequest?.filteration?.default_cron_start_time || "none",
      );
    } else {
      // No filteration data - reset all fields
      setFilterationType("none");
      setStartKey("");
      setEndKey("");
      setCustomDateFormat("");
      setLogCollectionPeriod("none");
    }
  }, [requestId]);

  // Auto-save pagination type when it changes
  React.useEffect(() => {
    if (
      !requestId ||
      isTyping.current ||
      isUpdatingFromUrl.current ||
      isUpdatingFromPagination.current ||
      !isPaginationLoaded.current
    )
      return;

    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: { paginationType },
      }),
    );
  }, [paginationType, requestId]);

  // Auto-save pagination form data when it changes
  React.useEffect(() => {
    if (
      !requestId ||
      isTyping.current ||
      isUpdatingFromUrl.current ||
      isUpdatingFromPagination.current ||
      !isPaginationLoaded.current
    )
      return;

    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: { paginationFormData },
      }),
    );
  }, [paginationFormData, requestId]);

  // Focus the input when the dialog opens
  React.useEffect(() => {
    if (mapRefInput.current) {
      mapRefInput.current.focus();
    }
  }, [showKeyDialog]);

  React.useEffect(() => {
    setIsResponse?.(false);
  }, []);

  // Enforce strict rule: pagination/filteration keys must never exist in query params.
  React.useEffect(() => {
    const sanitized = sanitizeQueryParams(queryParams);
    if (JSON.stringify(sanitized) === JSON.stringify(queryParams)) return;

    setQueryParams(
      sanitized.length > 0
        ? sanitized
        : [{ enabled: true, key: "", value: "" }],
    );
  }, [queryParams, sanitizeQueryParams]);

  // Function to add a new header input
  const addHeader = () => {
    setHeaders([...headers, { key: "", value: "" }]);
  };

  // Function to remove a header input
  const removeHeader = (index: number) => {
    const updated = [...headers];
    updated.splice(index, 1);
    setHeaders(updated);
  };

  const cleanedVariables = (definedVariables || []).map((obj) => {
    const copy = { ...obj };
    delete copy.value;
    return copy;
  });

  // Function to send API request
  const sendApiRequest = async () => {
    setLoading(true);
    setIsResponse?.(false);

    try {
      // Check for body validation errors before proceeding
      if (bodyValidationError) {
        setLoading(false);
        toast({
          title: "Validation Error",
          description: bodyValidationError,
          variant: "destructive",
          duration: 5000,
        });
        return;
      }

      // Additional validation for JSON bodies with array/object variables
      if (bodyType === "raw" && rawBodyType === "json" && body) {
        const varUsageValidation = validateVariableUsageInJSON(
          body,
          definedVariables,
        );
        if (!varUsageValidation.isValid) {
          setLoading(false);
          setBodyValidationError(varUsageValidation.error);
          toast({
            title: "Variable Usage Error",
            description: varUsageValidation.error,
            variant: "destructive",
            duration: 5000,
          });
          return;
        }
      }

      // Update finalRequestData with current headers before sending
      if (requestId) {
        setFinalRequestData((prev: any) =>
          updateRequestInFinalData({
            prev,
            requestId,
            updates: { headers: headers },
          }),
        );
      }

      // Save raw headers
      const rawHeaders = headers.filter((h) => h.key.trim() !== "");

      // Raw (user-input) values
      const rawUrl = targetUrl;
      const rawBody = body;
      const rawToken = authToken;

      // Check if config variables are present in the request
      const hasConfigVariables =
        /\{\{\s*config\.\w+\s*\}\}/.test(rawUrl) ||
        /\{\{\s*config\.\w+\s*\}\}/.test(rawBody) ||
        /\{\{\s*config\.\w+\s*\}\}/.test(rawToken) ||
        rawHeaders.some(
          (h) =>
            /\{\{\s*config\.\w+\s*\}\}/.test(h.key) ||
            /\{\{\s*config\.\w+\s*\}\}/.test(h.value),
        );

      // Get configuration forms count
      const configFormsCount = configPageData?.data
        ? Object.values(configPageData.data).reduce(
            (total: number, items: any) =>
              total + (Array.isArray(items) ? items.length : 0),
            0,
          )
        : 0;

      // If config variables exist, multiple configurations available, but no configuration is selected
      if (hasConfigVariables && configFormsCount > 1 && !selectedConfigData) {
        setLoading(false);
        setIsResponse?.(false);
        toast({
          title: "Configuration Required",
          description:
            "This request contains configuration variables. Please select a configuration before sending the request.",
          variant: "destructive",
          duration: 4000,
        });
        return;
      }

      const interpolatedUrl = interpolateDefinedVariablesOnly(
        rawUrl,
        definedVariables,
      );

      // Handle body based on bodyType and method
      const methodsWithoutBody = ["GET", "HEAD"];
      const shouldSkipBody = methodsWithoutBody.includes(method.toUpperCase());

      let interpolatedBody = "";
      if (!shouldSkipBody) {
        if (bodyType === "raw") {
          interpolatedBody = interpolateDefinedVariablesOnly(
            rawBody,
            definedVariables,
          );
        } else if (bodyType === "form-urlencoded") {
          // Convert form data to URL-encoded format
          const enabledFormData = formData.filter(
            (item) => item.enabled && item.key,
          );
          interpolatedBody = enabledFormData
            .map((item) => {
              const key = interpolateDefinedVariablesOnly(
                item.key,
                definedVariables,
              );
              const value = interpolateDefinedVariablesOnly(
                item.value,
                definedVariables,
              );
              return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
            })
            .join("&");
        } else {
          interpolatedBody = "";
        }
      }

      const interpolatedToken = interpolateDefinedVariablesOnly(
        rawToken,
        definedVariables,
      );

      const interpolatedHeaders = rawHeaders.map((h) => ({
        key: interpolateDefinedVariablesOnly(h.key, definedVariables),
        value: interpolateDefinedVariablesOnly(h.value, definedVariables),
      }));

      // Add Content-Type header for form-urlencoded if not already present
      if (bodyType === "form-urlencoded") {
        const hasContentType = interpolatedHeaders.some(
          (h) => h.key.toLowerCase() === "content-type",
        );
        if (!hasContentType) {
          interpolatedHeaders.push({
            key: "Content-Type",
            value: "application/x-www-form-urlencoded",
          });
        }
      }

      // Add Content-Type header for raw body types if not already present
      if (bodyType === "raw") {
        const hasContentType = interpolatedHeaders.some(
          (h) => h.key.toLowerCase() === "content-type",
        );
        if (!hasContentType) {
          let contentType = "text/plain";
          switch (rawBodyType) {
            case "json":
              contentType = "application/json";
              break;
            case "xml":
              contentType = "application/xml";
              break;
            case "html":
              contentType = "text/html";
              break;
            case "javascript":
              contentType = "application/javascript";
              break;
            case "text":
            default:
              contentType = "text/plain";
              break;
          }
          interpolatedHeaders.push({
            key: "Content-Type",
            value: contentType,
          });
        }
      }

      let authPayload: any = {
        type: "none",
        token: interpolatedToken || "",
        username: "",
        password: "",
      };

      if (showAuth) {
        if (authHeader === "basic") {
          // Basic auth always uses the specified type even if fields are empty
          authPayload = {
            type: "basic_auth",
            username,
            password,
          };
        } else if (authHeader === "none") {
          authPayload = {
            type: "none",
            token: "",
          };
        } else {
          // For all other auth types (bearer, authorization, api-key, jwtBearer, etc.)
          const resolvedToken = interpolateDefinedVariablesOnly(
            rawToken,
            definedVariables,
          );

          // Send the auth type even if token is empty (could be a config variable)
          authPayload = {
            type: mapAuthTypeToBackend(authHeader),
            token: resolvedToken || rawToken,
          };
        }
      }

      // Transform pagination from array to backend-expected format
      const firstPagination =
        paginationFormData && paginationFormData.length > 0
          ? paginationFormData[0]
          : null;

      // Construct pagination parameter with backend-expected field names
      const buildPaginationParameter = (
        firstPagination: PaginationParamRow,
      ) => {
        // Handle different pagination types from paginationType state
        if (paginationType === "cursor") {
          // Cursor pagination: limit_key, limit_value, cursor_key, cursor_value
          const limitKey =
            firstPagination.limitKey === "custom"
              ? firstPagination.limitCustomKey || "limit"
              : firstPagination.limitKey || "limit";
          const limitValue = firstPagination.limitValue || "";
          const cursorKey = firstPagination.cursorKey || "cursor";
          const cursorValue = firstPagination.cursorSourcePath || "";

          return {
            type: "cursor_pagination",
            parameter: {
              limit_key: limitKey,
              limit_value: limitValue,
              cursor_key: cursorKey,
              cursor_value: cursorValue,
            },
          };
        } else if (paginationType === "token") {
          // Token pagination: limit_key, limit_value, token_key, token_value
          const limitKey =
            firstPagination.limitKey === "custom"
              ? firstPagination.limitCustomKey || "limit"
              : firstPagination.limitKey || "limit";
          const limitValue = firstPagination.limitValue || "";
          const tokenKey = firstPagination.tokenKey || "token";
          const tokenValue = firstPagination.tokenSourcePath || "";

          return {
            type: "token_pagination",
            parameter: {
              limit_key: limitKey,
              limit_value: limitValue,
              token_key: tokenKey,
              token_value: tokenValue,
            },
          };
        } else if (paginationType === "next_url") {
          // Next URL pagination: limit_key, limit_value, next_url_key, next_url_value
          const limitKey =
            firstPagination.limitKey === "custom"
              ? firstPagination.limitCustomKey || "limit"
              : firstPagination.limitKey || "limit";
          const limitValue = firstPagination.limitValue || "";
          const nextUrlKey = firstPagination.nextUrlKey || "next_url";
          const nextUrlValue = ""; // Next URL comes from API response

          return {
            type: "next_url_pagination",
            parameter: {
              limit_key: limitKey,
              limit_value: limitValue,
              next_url_key: nextUrlKey,
              next_url_value: nextUrlValue,
            },
          };
        } else if (paginationType === "offset_limit") {
          // Offset-Limit pagination: offset_key, offset_value, limit_key, limit_value
          const categoryKey =
            firstPagination.categoryTypeKey === "custom"
              ? firstPagination.categoryTypeCustom || "offset"
              : firstPagination.categoryTypeKey || "offset";
          const categoryValue = firstPagination.categoryTypeValue || "";
          const paginationKey =
            firstPagination.paginationTypeKey === "custom"
              ? firstPagination.paginationTypeCustom || "limit"
              : firstPagination.paginationTypeKey || "limit";
          const paginationValue = firstPagination.paginationTypeValue || "";

          return {
            type: "offset_limit_pagination",
            parameter: {
              offset_key: categoryKey,
              offset_value: categoryValue,
              limit_key: paginationKey,
              limit_value: paginationValue,
            },
          };
        } else if (paginationType === "page_page_size") {
          // Page-Size pagination: page_key, page_value, per_page_key, per_page_value
          const categoryKey =
            firstPagination.categoryTypeKey === "custom"
              ? firstPagination.categoryTypeCustom || "page"
              : firstPagination.categoryTypeKey || "page";
          const categoryValue = firstPagination.categoryTypeValue || "";
          const paginationKey =
            firstPagination.paginationTypeKey === "custom"
              ? firstPagination.paginationTypeCustom || "per_page"
              : firstPagination.paginationTypeKey || "per_page";
          const paginationValue = firstPagination.paginationTypeValue || "";

          return {
            type: "page_size_pagination",
            parameter: {
              page_key: categoryKey,
              page_value: categoryValue,
              per_page_key: paginationKey,
              per_page_value: paginationValue,
            },
          };
        }

        // Default fallback to none
        return {
          type: "none",
        };
      };

      // Construct pagination object matching backend schema
      // Only enable pagination if:
      // 1. Params section is visible (showParams = true)
      // 2. Checkbox is checked (paginationEnable = true)
      // 3. A pagination type is selected
      // 4. Has actual field values
      const hasPaginationValues = () => {
        if (!firstPagination) return false;

        // Check based on pagination type
        if (paginationType === "offset_limit") {
          return !!(
            firstPagination.categoryTypeKey ||
            firstPagination.categoryTypeValue ||
            firstPagination.paginationTypeKey ||
            firstPagination.paginationTypeValue
          );
        } else if (paginationType === "page_page_size") {
          return !!(
            firstPagination.categoryTypeKey ||
            firstPagination.categoryTypeValue ||
            firstPagination.paginationTypeKey ||
            firstPagination.paginationTypeValue
          );
        } else if (paginationType === "cursor") {
          return !!(
            firstPagination.limitKey ||
            firstPagination.limitValue ||
            firstPagination.limitCustomKey ||
            firstPagination.cursorKey ||
            firstPagination.cursorValue
          );
        } else if (paginationType === "token") {
          return !!(
            firstPagination.limitKey ||
            firstPagination.limitValue ||
            firstPagination.limitCustomKey ||
            firstPagination.tokenKey ||
            firstPagination.tokenValue
          );
        } else if (paginationType === "next_url") {
          return !!(
            firstPagination.limitKey ||
            firstPagination.limitValue ||
            firstPagination.limitCustomKey ||
            firstPagination.nextUrlKey ||
            firstPagination.nextUrlValue
          );
        }

        return false;
      };

      // Check if pagination is explicitly disabled or has no type/values
      const isPaginationDisabled =
        !showParams ||
        !firstPagination ||
        !firstPagination.paginationEnable ||
        !paginationType ||
        paginationType === "" ||
        !hasPaginationValues();

      const paginationPayload = !isPaginationDisabled
        ? buildPaginationParameter(firstPagination)
        : {
            type: "none" as const,
            parameter: null,
          };

      // Build params array from queryParams with interpolation
      const paramsArray = safeQueryParams
        .filter((param) => param.enabled && param.key && param.value)
        .map((param) => ({
          key: interpolateDefinedVariablesOnly(param.key, definedVariables),
          value: interpolateDefinedVariablesOnly(param.value, definedVariables),
        }));

      // For execute endpoint, params is a simple dict
      const paramsDict: Record<string, string> = {};
      paramsArray.forEach((param) => {
        paramsDict[param.key] = param.value;
      });

      // For execute endpoint, use the full pagination payload
      const executePaginationPayload = !isPaginationDisabled
        ? buildPaginationParameter(firstPagination)
        : { type: "none" as const };

      // Format body based on body type
      let formattedBody: any = null;
      if (!shouldSkipBody) {
        if (bodyType === "raw") {
          // For raw body, use the interpolated body string
          formattedBody = interpolatedBody;
        } else if (bodyType === "form-urlencoded") {
          // For form-urlencoded, convert to object format
          const enabledFormData = formData.filter(
            (item) => item.enabled && item.key,
          );
          formattedBody = {};
          enabledFormData.forEach((item) => {
            const key = interpolateDefinedVariablesOnly(
              item.key,
              definedVariables,
            );
            const value = interpolateDefinedVariablesOnly(
              item.value,
              definedVariables,
            );
            formattedBody[key] = value;
          });
        }
      }

      const mappedBodyType =
        bodyType === "form-urlencoded" ? "x-www-form-urlencoded" : bodyType;

      const baseRequest: Partial<CommonApiRequestPayload> = {
        requestId,
        plugin_id: pluginId || "",
        url: interpolatedUrl,
        method,
        params: {
          type: paramsArray.length > 0 ? "params" : "none",
          parameter: paramsArray,
        },
        headers: interpolatedHeaders,
        configuration_Id: selectedConfigData?.uid || Date.now().toString(),
        auth: authPayload,
        ssl_verify: sslVerify,
        body: formattedBody,
        body_type: mappedBodyType,
        raw_type: bodyType === "raw" ? rawBodyType : null,
        pagination: executePaginationPayload,
        filteration:
          filterationType && filterationType !== "none"
            ? {
                type:
                  filterationType === "custom"
                    ? "custom_date_format"
                    : filterationType,
                default_cron_start_time: logCollectionPeriod || "none",
                start_key: startKey || "",
                end_key: endKey || "",
                custom_date_format: customDateFormat || "",
              }
            : {
                type: "none",
                default_cron_start_time: "none",
              },
        loopVariable: null,
        loopSourcePath: null,
        checkpoint: {
          produces: {
            enabled: false,
            sourcePath: null,
          },
          consumes: {
            enabled: false,
            paramTemplate: null,
          },
        },
        chainedFrom: null,
        flag: 1,
        isLoop: false,
        config: config ?? {},
        data: data ?? {},
      };

      //  Save raw values for prefill
      const rawRequestData = {
        requestId,
        plugin_id: pluginId || "",
        url: rawUrl,
        originalUrl: rawUrl,
        method,
        params:
          paramsArray.length > 0
            ? {
                type: "params",
                parameter: paramsArray,
              }
            : {
                type: "none",
                parameter: [],
              },
        body: rawBody || null,
        bodyType: bodyType,
        rawBodyType: rawBodyType,
        formData: formData,
        headers: rawHeaders,
        auth:
          authHeader === "basic"
            ? {
                type: mapAuthTypeToBackend("basic"),
                username: username || "",
                password: password || "",
                token: "",
              }
            : {
                type: mapAuthTypeToBackend(authHeader) || "bearer",
                token: rawToken || "",
                username: "",
                password: "",
              },
        ssl_verify: sslVerify,
        showAuth: showAuth,
        paginationType: paginationType || "",
        paginationFormData: paginationFormData,
        queryParams: safeQueryParams,
        pagination: paginationPayload,
        filteration:
          filterationType && filterationType !== "none"
            ? {
                type:
                  filterationType === "custom"
                    ? "custom_date_format"
                    : filterationType,
                parameter: null,
                default_cron_start_time: logCollectionPeriod || "none",
                start_key: startKey || "",
                end_key: endKey || "",
                custom_date_format: customDateFormat || "",
              }
            : {
                type: "none",
                parameter: null,
                default_cron_start_time: "none",
              },
        configuration_Id: selectedConfigData?.uid || Date.now().toString(),
        isLoop: false,
        loopVariable: null as string | null,
        loopSourcePath: null as string | null,
        loopSourceRequest: null as number | null,
        loopSourceRequestBundle: null as string | null,
      };
      // Validate body content before sending request
      if (bodyType === "raw" && body) {
        const bodyValidation = validateBodyContent(body, rawBodyType);
        if (!bodyValidation.isValid) {
          toast({
            variant: "destructive",
            title: "Body Validation Failed",
            description:
              bodyValidation.error || "Invalid body format for selected type.",
          });
          setLoading(false);
          setIsResponse?.(false);
          return;
        }
      }

      const validation = validateLoopingFromUrl(rawUrl, definedVariables || []);
      if (!validation.isValid) {
        toast({
          variant: "destructive",
          title: "Validation Failed",
          description: validation.error,
        });
        setLoading(false);
        setIsResponse?.(false);
        return;
      }

      const isLooping = /\[\*\]/.test(rawUrl);

      if (isLooping) {
        const loopValues = validation.loopValues;
        if (!loopValues || loopValues.length === 0) {
          toast({
            variant: "destructive",
            title: "Error",
            description: "No valid values found for looping.",
          });
          return;
        }

        const allResults: any[] = [];
        const loopPayloads: Partial<CommonApiRequestPayload>[] = [];

        // Dynamically extract loopSourcePath and loopVariable from URL
        const loopMatch = rawUrl.match(/{{\s*(\w+)\[\*\]\.(\w+)\s*}}/);
        const loopSourcePath = loopMatch?.[1] ?? null; // e.g., 'values'
        const loopVarName = loopMatch?.[2] ?? null; // e.g., 'id'

        rawRequestData.isLoop = true;
        rawRequestData.loopVariable = loopVarName;
        rawRequestData.loopSourcePath = loopSourcePath;
        rawRequestData.loopSourceRequest = requestId ?? null;
        rawRequestData.loopSourceRequestBundle =
          selectedLogicBlockId != null ? String(selectedLogicBlockId) : null;

        const aggregatedMetrics: any = {
          totalRequests: 0,
          totalDuration: 0,
          avgDuration: 0,
          totalSize: 0,
          totalRequestSize: 0,
          avgSize: 0,
          avgRequestSize: 0,
          requests: [],
          // Summary metrics from last successful request
          status: null,
          statusText: null,
          durationMs: null,
          requestSizeKB: null,
          responseSizeKB: null,
          speedKBps: null,
          contentType: null,
        };

        for (let i = 0; i < loopValues.length; i++) {
          const val = loopValues[i];

          const loopUrl = rawUrl.replace(/{{\s*[^{}]*\[\*\]\.[^{}]*\s*}}/, val);
          const interpolatedLoopUrl = loopUrl;

          toast({
            variant: "success",
            title: "Looping...",
            description: `Fetching requests  ${i + 1}/${loopValues.length}`,
          });

          const loopPayload: Partial<CommonApiRequestPayload> = {
            ...baseRequest,
            url: interpolatedLoopUrl,
            isLoop: true,
            loopVariable: loopVarName,
            loopSourcePath: loopSourcePath,
            loopSourceRequest: requestId ?? null,
            loopSourceRequestBundle:
              selectedLogicBlockId != null
                ? String(selectedLogicBlockId)
                : null,
          };

          updateRequest?.(requestId, {
            isLoop: true,
            loopVariable: loopVarName,
            loopSourcePath: loopSourcePath,
            loopSourceRequest: rawRequestData.loopSourceRequest,
            loopSourceRequestBundle: rawRequestData.loopSourceRequestBundle,
          });

          loopPayloads.push(loopPayload);

          const res: any = await sendRequest(loopPayload);
          const singleData = res?.body?.data ?? {};

          // Normalize the data and push only once
          const normalizedData = normalizeResponseData(singleData);
          allResults.push(normalizedData);

          //  Collect metrics from each request
          if (res?.metrics) {
            aggregatedMetrics.totalRequests++;
            aggregatedMetrics.totalDuration +=
              parseFloat(res?.metrics?.durationMs) || 0;
            aggregatedMetrics.totalSize +=
              parseFloat(res?.metrics?.responseSizeKB) || 0;

            // Store metrics from the last successful request for display
            aggregatedMetrics.status = res?.metrics?.status;
            aggregatedMetrics.statusText = res?.metrics?.statusText;

            // Capture content-type from response
            if (res?.headers?.["content-type"]) {
              aggregatedMetrics.contentType = res.headers["content-type"];
            } else if (res?.body?.headers?.["content-type"]) {
              aggregatedMetrics.contentType = res.body.headers["content-type"];
            }

            aggregatedMetrics.requests.push({
              iteration: i + 1,
              url: interpolatedLoopUrl,
              ...res.metrics,
            });
          }
        }

        //  Calculate average duration and size
        if (aggregatedMetrics.totalRequests > 0) {
          aggregatedMetrics.avgDuration =
            aggregatedMetrics.totalDuration / aggregatedMetrics.totalRequests;
          aggregatedMetrics.avgSize =
            aggregatedMetrics.totalSize / aggregatedMetrics.totalRequests;

          // Set display metrics as averages
          aggregatedMetrics.durationMs =
            aggregatedMetrics.avgDuration.toFixed(2);
          aggregatedMetrics.responseSizeKB =
            aggregatedMetrics.avgSize.toFixed(2);

          // Calculate average speed if we have duration
          if (aggregatedMetrics.avgDuration > 0) {
            aggregatedMetrics.speedKBps = (
              (aggregatedMetrics.avgSize / aggregatedMetrics.avgDuration) *
              1000
            ).toFixed(2);
          }
        }

        //  Create response with metrics
        const loopResponseData = {
          data: allResults,
          metrics: aggregatedMetrics,
        };

        setResponse?.(loopResponseData);
        setBlockResponse(blockId, loopResponseData);

        setFinalRequestData((prev: any) => {
          const existingRequests = prev?.request || [];
          const reqIndex = existingRequests.findIndex(
            (req: any) => req.requestId === requestId,
          );

          const updatedRequests =
            reqIndex !== -1
              ? [
                  ...existingRequests.slice(0, reqIndex),
                  rawRequestData,
                  ...existingRequests.slice(reqIndex + 1),
                ]
              : [...existingRequests, rawRequestData];

          return {
            request: updatedRequests,
            variables: cleanedVariables,
            responses: {
              ...(prev?.responses || {}),
              [requestId ?? 0]: loopResponseData,
            },
          };
        });

        toast({
          variant: "success",
          title: "Success",
          description: "Loop request completed successfully.",
          duration: 2000,
        });
        setIsResponse?.(true);
      } else {
        //  SINGLE REQUEST LOGIC
        const result = await sendRequest(baseRequest);

        const rawData = result?.body?.data ?? {};

        //  Normalize the response data structure (convert numeric keys to array)
        const normalizedData = normalizeResponseData(rawData);

        //  Sanitize string values recursively
        const sanitizeValue = (val: any): any => {
          if (Array.isArray(val)) {
            return val.map(sanitizeValue);
          }
          if (val && typeof val === "object") {
            return Object.fromEntries(
              Object.entries(val).map(([k, v]) => [
                k,
                typeof v === "string" ? v.trim() : sanitizeValue(v),
              ]),
            );
          }
          return typeof val === "string" ? val.trim() : val;
        };

        const sanitizedData = sanitizeValue(normalizedData);

        //  Create response object with both data and metrics
        const responseData = {
          data: sanitizedData,
          metrics: result?.metrics || null,
        };

        setResponse?.(responseData);
        setBlockResponse?.(blockId, responseData);

        setFinalRequestData((prev: any) => {
          const existingRequests = prev?.request || [];
          const reqIndex = existingRequests.findIndex(
            (req: any) => req.requestId === requestId,
          );

          const updatedRequests =
            reqIndex !== -1
              ? [
                  ...existingRequests.slice(0, reqIndex),
                  rawRequestData,
                  ...existingRequests.slice(reqIndex + 1),
                ]
              : [...existingRequests, rawRequestData];

          return {
            request: updatedRequests,
            variables: cleanedVariables,
            responses: {
              ...(prev?.responses || {}),
              [requestId ?? 0]: responseData,
            },
          };
        });

        toast({
          variant: "success",
          title: "Request Successful",
          duration: 2000,
        });
        setIsResponse?.(true);
      }

      setErrors((prev) => ({
        ...prev,
        [String(selectedLogicBlockId)]: {
          ...(prev[String(selectedLogicBlockId)] || {}),
          [String(requestId)]: null,
        },
      }));
      // setError(null);
    } catch (err: any) {
      setIsResponse?.(false);

      // Extract the actual error message from the API response
      let errorMessage = "An unknown error occurred";

      if (err?.response?.data?.message) {
        // If API returns structured error with message
        errorMessage = err.response.data.message;
      } else if (err?.response?.data?.error) {
        // If API returns error field
        errorMessage = err.response.data.error;
      } else if (err?.response?.data) {
        // If API returns error as object, stringify it
        errorMessage =
          typeof err.response.data === "string"
            ? err.response.data
            : JSON.stringify(err.response.data);
      } else if (err?.message) {
        // Fallback to error message
        errorMessage = err.message;
      } else if (typeof err === "string") {
        errorMessage = err;
      } else if (err?.toString) {
        errorMessage = err.toString();
      }

      console.error(" API Request Error:", {
        fullError: err,
        extractedMessage: errorMessage,
        response: err?.response,
        data: err?.response?.data,
      });

      setErrors((prev) => ({
        ...prev,
        [String(selectedLogicBlockId)]: {
          ...(prev[String(selectedLogicBlockId)] || {}),
          [String(requestId)]: errorMessage, // Store the extracted error message
        },
      }));

      setResponse?.(null);

      showToast({
        variant: "destructive",
        title: "Request Failed",
        description: errorMessage,
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle selection change
  const handleSelectVariablesOption = (value: string) => {
    setSelectedOption(value);
    if (value === "common" || value === "none") {
      setIsLogMapping(false);
      setIsCommonVar(true);
    } else if (value === "saveLogs") {
      setIsLogMapping(true);
      setIsCommonVar(false);
    } else if (value === "saveDataList") {
      setIsLogMapping(true);
      setIsCommonVar(false);
    } else {
      setIsLogMapping(false);
      setIsCommonVar(false);
    }
  };

  // Memoize filteration change handler to prevent infinite re-renders
  const handleFilterationChange = React.useCallback(
    (
      type: string,
      start: string,
      end: string,
      customFormat: string,
      logCollection: string,
    ) => {
      setFilterationType(type);
      setStartKey(start);
      setEndKey(end);
      setCustomDateFormat(customFormat);
      setLogCollectionPeriod(logCollection || "one_hour");
    },
    [],
  );

  // Function to render JSON data recursively
  React.useEffect(() => {
    const cleanedVariables = (definedVariables || []).map((obj) => {
      const copy = { ...obj };
      delete copy.value;
      return copy;
    });
    setFinalRequestData((prev: any) => ({
      ...prev,
      variables: cleanedVariables, // only update this
    }));
  }, [definedVariables]);

  // Function to render JSON data recursively
  const renderJson = (
    data: any,
    level = 0,
    pathPrefix = "",
  ): React.ReactNode => {
    const indent = { marginLeft: `${level * 10}px` };

    // Handle arrays
    if (Array.isArray(data)) {
      return (
        <div style={indent}>
          <span
            className="text-primary cursor-pointer hover:text-primaryBlue font-semibold text-lg"
            onClick={() => {
              setSelectedKey(pathPrefix === "" ? "root" : pathPrefix);
              setShowKeyDialog(true);
              setSelectedValue(data);
            }}
          >
            [
          </span>
          {data.length > 0 ? (
            data.map((item, index) => {
              // Use bracket notation for array indices
              const itemPath =
                pathPrefix === ""
                  ? `[${index}]` // Root level array: [0]
                  : `${pathPrefix}[${index}]`; // Nested array: path[0]

              return (
                <div key={index} style={{ marginLeft: 16 }}>
                  {typeof item === "object" && item !== null ? (
                    renderJson(item, level + 1, itemPath)
                  ) : (
                    <span
                      onClick={() => {
                        setSelectedKey(itemPath);
                        setShowKeyDialog(true);
                        setSelectedValue(item);
                      }}
                      className="text-muted-foreground cursor-pointer hover:text-primaryBlue"
                    >
                      <span> &quot;{String(item)}&quot; </span>
                    </span>
                  )}
                  {index < data.length - 1 && <span>,</span>}
                </div>
              );
            })
          ) : (
            <span className="text-muted-foreground ml-2">empty</span>
          )}
          <span
            className="text-primary cursor-pointer hover:text-primaryBlue font-semibold"
            onClick={() => {
              setSelectedKey(pathPrefix || "root");
              setShowKeyDialog(true);
              setSelectedValue(data);
            }}
          >
            ]
          </span>
        </div>
      );
    }

    // Handle objects
    if (typeof data === "object" && data !== null) {
      const entries = Object.entries(data).filter(
        ([, value]) =>
          value !== null &&
          !(
            (typeof value === "object" &&
              !Array.isArray(value) &&
              Object.keys(value).length === 0) ||
            (Array.isArray(value) && value.length === 0)
          ),
      );

      if (entries.length === 0) return null;

      return (
        <div style={indent}>
          <div>
            <span
              className="cursor-pointer text-primary text-lg font-semibold hover:text-primaryBlue"
              onClick={() => {
                setShowKeyDialog(true);
                setSelectedKey(pathPrefix === "" ? "root" : pathPrefix);
                setSelectedValue(data);
              }}
            >
              {"{"}
            </span>
          </div>
          {entries.map(([key, value], idx) => {
            let fullPath: string;

            if (pathPrefix === "") {
              // Root level property: userId
              fullPath = key;
            } else if (pathPrefix.startsWith("[")) {
              // After array index: [0].userId
              fullPath = `${pathPrefix}.${key}`;
            } else if (pathPrefix === "root") {
              // From root: root.userId
              fullPath = `root.${key}`;
            } else {
              // Nested property: path.subpath.key
              fullPath = `${pathPrefix}.${key}`;
            }

            return (
              <div key={key} style={{ marginLeft: 16 }}>
                <span
                  onClick={() => {
                    if (
                      typeof value !== "object" ||
                      value === null ||
                      Array.isArray(value)
                    ) {
                      setSelectedKey(fullPath);
                      setShowKeyDialog(true);
                      setSelectedValue(value);
                    }
                  }}
                  className="text-primary cursor-pointer hover:text-primaryBlue font-semibold"
                >
                  <span>&quot;{key}&quot;</span>
                </span>
                :{" "}
                {typeof value === "object" && value !== null ? (
                  renderJson(value, level + 1, fullPath)
                ) : (
                  <span className="text-muted-foreground cursor-pointer">
                    <span>&quot;{String(value)}&quot;</span>
                  </span>
                )}
                {idx < entries.length - 1 && <span>,</span>}
              </div>
            );
          })}
          <span
            className="text-primary font-semibold cursor-pointer hover:text-primaryBlue"
            onClick={() => {
              setShowKeyDialog(true);
              setSelectedKey(pathPrefix || "root");
              setSelectedValue(data);
            }}
          >
            {"}"}
          </span>
        </div>
      );
    }

    // Handle primitives
    return (
      <span
        className="text-muted-foreground cursor-pointer hover:text-primaryBlue"
        onClick={() => {
          setSelectedKey(pathPrefix || "root");
          setShowKeyDialog(true);
          setSelectedValue(data);
        }}
      >
        <span>&quot;{String(data)}&quot;</span>
      </span>
    );
  };

  // Function to handle key mapping dialog submission
  const handleAddVariableMapping = () => {
    if (!inputName.trim() || !selectedKey) return;

    const newMapping: Partial<DefinedVariable> = {
      id: Date.now(),
      name: inputName.trim(),
      type: "single",
      scope: isCommonVar ? "global" : "local",
      sourceRequestId: requestId,
      sourcePath: selectedKey === "root" ? "root" : selectedKey,
      value: selectedValue,
      isCommon: isCommonVar,
      variableType:
        selectedLogicName == "Data List" ? "Data list" : selectedLogicName,
      sourceBundleId: selectedLogicBlockId,
      isAuthentication: isAuthVar ? true : false,
    };

    setDefinedVariables?.((prev: DefinedVariable[]) => {
      // Filter variables by current bundleId
      const currentBundleVars = prev.filter(
        (v) => v.sourceBundleId === newMapping.sourceBundleId,
      );

      const nameExistsInBundle = currentBundleVars.some(
        (v) => v.name === newMapping.name,
      );

      const valueExistsInBundle = currentBundleVars.some(
        (v) =>
          v.sourceRequestId === newMapping.sourceRequestId &&
          v.sourcePath === newMapping.sourcePath &&
          JSON.stringify(v.value) === JSON.stringify(newMapping.value),
      );

      // return if same sourcePath is mapping from same request
      const sourcePathExistsInBundle = currentBundleVars.some(
        (v) => v.sourcePath === newMapping.sourcePath,
      );

      if (sourcePathExistsInBundle) {
        const currentSourcePathVar = currentBundleVars.find(
          (v) => v.sourcePath === newMapping.sourcePath,
        );

        if (
          currentSourcePathVar?.sourceRequestId === newMapping.sourceRequestId
        ) {
          toast({
            variant: "destructive",
            title: "Duplicate Source Path",
            description: `"${newMapping.sourcePath}" variable is alredy saved as {{${currentSourcePathVar?.name}}}`,
          });
          // Reset dialog state
          setShowKeyDialog(false);
          setInputName("");
          setIsAuthVar(false);
          setSelectedKey(null);
          setIsCommonVar(false);
          return prev;
        }
      }

      if (nameExistsInBundle) {
        toast({
          variant: "destructive",
          title: "Duplicate Name",
          description: `A variable with the name "${newMapping.name}" already exists in this logic block.`,
        });
        return prev;
      }

      if (valueExistsInBundle) {
        toast({
          variant: "destructive",
          title: "Duplicate Mapping",
          description: `This value is already mapped in this logic block.`,
        });
        // Reset dialog state
        setShowKeyDialog(false);
        setInputName("");
        setIsAuthVar(false);
        setSelectedKey(null);
        setIsCommonVar(false);
        return prev;
      }

      // copy the new added variable
      const newValue = `{{${inputName}}}`;

      toast({
        variant: "success",
        title: "Key Mapped",
        description: `Key "${selectedKey}" mapped as "${inputName}" and the ${newValue} copied to clipboard.`,
        duration: 2000,
      });

      // Reset dialog state
      setShowKeyDialog(false);
      setInputName("");
      setIsAuthVar(false);
      setSelectedKey(null);
      setIsCommonVar(false);

      navigator.clipboard.writeText(newValue).catch(() => {});

      return [...prev, newMapping];
    });
  };

  //  mappin set up
  const handleSetMappingData = (blockId: any) => {
    if (!selectedKey) return;
    const trimmedName = inputName.trim();

    const newField = {
      fieldId: Date.now(), // or a unique integer
      rawFieldName: selectedKey, // this should be the path in the response
      // type: typeof selectedValue, // or "string", "number", etc.
      mappingLsField: trimmedName, // the mapped field name
      value: selectedValue,
      requestId,
      logicBlockId: blockId,
    };

    setLogMappedFields?.((prev) => {
      const bundleFields = prev[blockId] || [];
      return {
        ...prev,
        [blockId]: [...bundleFields, [newField]], // store as array of arrays for mappingFields
      };
    });

    // Pass the selected value and key (path) directly to the mapping dialog
    // If selectedValue is already an object/array, use it as is
    setSelectedMappingPath({ value: selectedValue, path: selectedKey });
    dispatch({
      type: MeshPluginActions.SET_OPEN_MAPFIELDS_DIALOG,
      payload: { open: true, requestId: requestId ?? null },
    });

    // Cleanup
    setShowKeyDialog(false);
    setInputName("");
    setSelectedKey(null);
    setIsCommonVar(false);
    setIsLogMapping(false);
  };

  React.useEffect(() => {
    if (showKeyDialog) {
      setSelectedOption("none");
    }
  }, [showKeyDialog]);

  // set the errors empty when mounts
  React.useEffect(() => {
    if (!requestId || !selectedLogicBlockId) return;

    setErrors((prev) => ({
      ...prev,
      [selectedLogicBlockId ?? "default"]: {
        ...(prev[selectedLogicBlockId ?? "default"] || {}),
        [requestId]: null,
      },
    }));
  }, [requestId, selectedLogicBlockId]);

  // get the error message for current request
  const errorMessage =
    selectedLogicBlockId != null && requestId != null
      ? errors?.[selectedLogicBlockId]?.[requestId]
      : undefined;

  // clear the response state when there is error
  React.useEffect(() => {
    if (!requestId || !selectedLogicBlockId) return;

    const scopedError = errors?.[selectedLogicBlockId]?.[requestId];

    if (scopedError) {
      // Clear stale response when there's an error
      setResponse?.(null);
      setIsResponse?.(false);
      setBlockResponse?.(blockId, null);

      setFinalRequestData((prev: any) => ({
        ...prev,
        responses: {
          ...(prev?.responses || {}),
          [requestId]: null,
        },
      }));
    }
  }, [errors, requestId, selectedLogicBlockId]);

  // function to update request in finalRequestData
  function updateRequestInFinalData({
    prev,
    requestId,
    updates,
  }: {
    prev: any;
    requestId: number | undefined;
    updates: Partial<any>;
  }) {
    if (!requestId) return prev;
    const requests = prev?.request ? [...prev.request] : [];
    const reqIndex = requests.findIndex(
      (req: any) => req.requestId === requestId,
    );

    //  Build complete auth object preserving all fields
    let authObj;

    if (updates.auth !== undefined) {
      // Merge with existing auth to preserve all fields
      // Map the type to backend format if it's coming from authHeader fallback
      const finalType = updates.auth.type
        ? updates.auth.type
        : requests[reqIndex]?.auth?.type
          ? requests[reqIndex].auth.type
          : mapAuthTypeToBackend(authHeader);

      authObj = {
        type: finalType,
        token:
          updates.auth.token ?? requests[reqIndex]?.auth?.token ?? authToken,
        username:
          updates.auth.username ??
          requests[reqIndex]?.auth?.username ??
          username,
        password:
          updates.auth.password ??
          requests[reqIndex]?.auth?.password ??
          password,
      };
    } else if (requests[reqIndex]?.auth) {
      authObj = requests[reqIndex].auth;
    } else {
      // Build from current state and map to backend format
      authObj = {
        type: mapAuthTypeToBackend(authHeader),
        token: authToken || "",
        username: username || "",
        password: password || "",
      };
    }

    // Build pagination object from paginationType and paginationFormData if they exist
    const finalPaginationType =
      updates.paginationType ??
      requests[reqIndex]?.paginationType ??
      paginationType;

    const finalPaginationFormData =
      updates.paginationFormData ??
      requests[reqIndex]?.paginationFormData ??
      paginationFormData;

    let paginationObj = {
      type: "none" as string,
      parameter: null as any,
    };

    // Only build pagination object if we have a valid type and form data
    if (
      finalPaginationType &&
      finalPaginationType !== "none" &&
      finalPaginationFormData &&
      finalPaginationFormData.length > 0
    ) {
      const enabledRow = finalPaginationFormData.find(
        (row: any) => row.paginationEnable,
      );

      if (enabledRow) {
        // Normalize type (add _pagination suffix for backend)
        const normalizedType = finalPaginationType.replace(/_pagination$/, "");

        if (normalizedType === "offset_limit") {
          const offsetKey =
            enabledRow.categoryTypeKey === "custom"
              ? enabledRow.categoryTypeCustom
              : enabledRow.categoryTypeKey;
          const limitKey =
            enabledRow.paginationTypeKey === "custom"
              ? enabledRow.paginationTypeCustom
              : enabledRow.paginationTypeKey;

          if (
            offsetKey &&
            enabledRow.categoryTypeValue &&
            limitKey &&
            enabledRow.paginationTypeValue
          ) {
            paginationObj = {
              type: "offset_limit_pagination",
              parameter: {
                offset_key: offsetKey,
                offset_value: enabledRow.categoryTypeValue,
                limit_key: limitKey,
                limit_value: enabledRow.paginationTypeValue,
              },
            };
          }
        } else if (
          normalizedType === "page_page_size" ||
          normalizedType === "page_size"
        ) {
          const pageKey =
            enabledRow.categoryTypeKey === "custom"
              ? enabledRow.categoryTypeCustom
              : enabledRow.categoryTypeKey;
          const pageSizeKey =
            enabledRow.paginationTypeKey === "custom"
              ? enabledRow.paginationTypeCustom
              : enabledRow.paginationTypeKey;

          if (
            pageKey &&
            enabledRow.categoryTypeValue &&
            pageSizeKey &&
            enabledRow.paginationTypeValue
          ) {
            paginationObj = {
              type: "page_size_pagination",
              parameter: {
                page_key: pageKey,
                page_value: enabledRow.categoryTypeValue,
                per_page_key: pageSizeKey,
                per_page_value: enabledRow.paginationTypeValue,
              },
            };
          }
        } else if (normalizedType === "cursor") {
          const limitKey =
            enabledRow.limitKey === "custom"
              ? enabledRow.limitCustomKey
              : enabledRow.limitKey;

          // Only require cursorKey - limit is optional
          if (enabledRow.cursorKey) {
            paginationObj = {
              type: "cursor_pagination",
              parameter: {
                limit_key: limitKey || "",
                limit_value: enabledRow.limitValue || "",
                cursor_key: enabledRow.cursorKey || "",
                cursor_value: enabledRow.cursorSourcePath || "",
              },
            };
          }
        } else if (normalizedType === "token") {
          const limitKey =
            enabledRow.limitKey === "custom"
              ? enabledRow.limitCustomKey
              : enabledRow.limitKey;

          // Only require tokenKey - limit is optional
          if (enabledRow.tokenKey) {
            paginationObj = {
              type: "token_pagination",
              parameter: {
                limit_key: limitKey || "",
                limit_value: enabledRow.limitValue || "",
                token_key: enabledRow.tokenKey || "",
                token_value: enabledRow.tokenSourcePath || "",
              },
            };
          }
        } else if (normalizedType === "next_url") {
          const limitKey =
            enabledRow.limitKey === "custom"
              ? enabledRow.limitCustomKey
              : enabledRow.limitKey;

          // Only require nextUrlKey - limit is optional
          if (enabledRow.nextUrlKey) {
            paginationObj = {
              type: "next_url_pagination",
              parameter: {
                limit_key: limitKey || "",
                limit_value: enabledRow.limitValue || "",
                next_url_key: enabledRow.nextUrlKey || "",
                next_url_value: "", // Next URL comes directly from API response
              },
            };
          }
        }

        // Fallback: If validation failed but we have a pagination type selected,
        // create a minimal pagination object to preserve the type
        if (paginationObj.type === "none" && normalizedType !== "none") {
          if (normalizedType === "offset_limit") {
            paginationObj = {
              type: "offset_limit_pagination",
              parameter: {
                offset_key: "",
                offset_value: "",
                limit_key: "",
                limit_value: "",
              },
            };
          } else if (
            normalizedType === "page_page_size" ||
            normalizedType === "page_size"
          ) {
            paginationObj = {
              type: "page_size_pagination",
              parameter: {
                page_key: "",
                page_value: "",
                per_page_key: "",
                per_page_value: "",
              },
            };
          } else if (normalizedType === "cursor") {
            const limitKey =
              enabledRow.limitKey === "custom"
                ? enabledRow.limitCustomKey || ""
                : enabledRow.limitKey || "";

            paginationObj = {
              type: "cursor_pagination",
              parameter: {
                limit_key: limitKey,
                limit_value: enabledRow.limitValue || "",
                cursor_key: enabledRow.cursorKey || "",
                cursor_value: enabledRow.cursorSourcePath || "",
              },
            };
          } else if (normalizedType === "token") {
            const limitKey =
              enabledRow.limitKey === "custom"
                ? enabledRow.limitCustomKey || ""
                : enabledRow.limitKey || "";

            paginationObj = {
              type: "token_pagination",
              parameter: {
                limit_key: limitKey,
                limit_value: enabledRow.limitValue || "",
                token_key: enabledRow.tokenKey || "",
                token_value: enabledRow.tokenSourcePath || "",
              },
            };
          } else if (normalizedType === "next_url") {
            const limitKey =
              enabledRow.limitKey === "custom"
                ? enabledRow.limitCustomKey || ""
                : enabledRow.limitKey || "";

            paginationObj = {
              type: "next_url_pagination",
              parameter: {
                limit_key: limitKey,
                limit_value: enabledRow.limitValue || "",
                next_url_key: enabledRow.nextUrlKey || "",
                next_url_value: "", // Next URL comes directly from API response
              },
            };
          }
        }
      }
    }

    // Always use the latest state for all fields
    const fullRequest = {
      ...(requests[reqIndex] || {}),
      requestId,
      url: updates.url ?? requests[reqIndex]?.url ?? targetUrl,
      originalUrl:
        updates.originalUrl ?? requests[reqIndex]?.originalUrl ?? targetUrl,
      method: updates.method ?? requests[reqIndex]?.method ?? method,
      params: updates.params ??
        requests[reqIndex]?.params ?? {
          type: "none",
          parameter: [],
        },
      body: updates.body ?? requests[reqIndex]?.body ?? body,
      bodyType: updates.bodyType ?? requests[reqIndex]?.bodyType ?? bodyType,
      rawBodyType:
        updates.rawBodyType ?? requests[reqIndex]?.rawBodyType ?? rawBodyType,
      formData: updates.formData ?? requests[reqIndex]?.formData ?? formData,
      headers: updates.headers ?? requests[reqIndex]?.headers ?? headers,
      auth: authObj,
      // Don't save showAuth - it should be calculated from auth values, not stored
      ssl_verify:
        updates.ssl_verify ?? requests[reqIndex]?.ssl_verify ?? sslVerify,
      paginationType:
        updates.paginationType ??
        requests[reqIndex]?.paginationType ??
        paginationType,
      paginationFormData:
        updates.paginationFormData ??
        requests[reqIndex]?.paginationFormData ??
        paginationFormData,
      queryParams:
        updates.queryParams ?? requests[reqIndex]?.queryParams ?? queryParams,
      pagination: paginationObj, // Use the built pagination object instead of preserving old one
      filteration: updates.filteration ??
        requests[reqIndex]?.filteration ?? {
          type: "none",
          parameter: null,
          default_cron_start_time: "none",
        },
      configuration_Id:
        selectedConfigData?.uid ??
        requests[reqIndex]?.configuration_Id ??
        Date.now().toString(),
      isLoop: updates.isLoop ?? requests[reqIndex]?.isLoop ?? false,
      loopVariable:
        updates.loopVariable ?? requests[reqIndex]?.loopVariable ?? null,
      loopSourcePath:
        updates.loopSourcePath ?? requests[reqIndex]?.loopSourcePath ?? null,
      loopSourceRequest:
        updates.loopSourceRequest ??
        requests[reqIndex]?.loopSourceRequest ??
        null,
      loopSourceRequestBundle:
        updates.loopSourceRequestBundle ??
        requests[reqIndex]?.loopSourceRequestBundle ??
        null,
    };

    if (reqIndex !== -1) {
      requests[reqIndex] = { ...fullRequest };
    } else {
      requests.push({ ...fullRequest });
    }
    return { ...prev, request: requests };
  }

  /**
   * Parse URL query string and separate pagination keys from query params
   * Similar to how Postman parses URLs - clean separation of concerns
   */
  const parseUrlQueryString = React.useCallback(
    (url: string, currentFormData: PaginationParamRow[]) => {
      const queryStringMatch = url.match(/\?(.+)$/);
      if (!queryStringMatch) {
        return {
          paginationParams: new Map<string, string>(),
          queryParams: [],
          paginationKeys: new Set<string>(),
        };
      }

      const queryString = queryStringMatch[1].trim();

      // Handle empty or invalid query strings
      if (
        !queryString ||
        queryString === "&" ||
        queryString === "=" ||
        /^[&=]+$/.test(queryString)
      ) {
        return {
          paginationParams: new Map<string, string>(),
          queryParams: [{ enabled: true, key: "", value: "" }],
          paginationKeys: new Set<string>(),
        };
      }

      // Manual parsing to handle incomplete params like "key1=value1&key2"
      const params = new URLSearchParams();
      const rawParams = queryString.split("&");

      rawParams.forEach((param) => {
        if (!param) return; // Skip empty params

        if (param.includes("=")) {
          // Normal param with key=value
          const [key, ...valueParts] = param.split("=");
          const value = valueParts.join("="); // Handle values with = in them
          params.append(key, value);
        } else {
          // Incomplete param with just key (no =)
          params.append(param, " ");
        }
      });

      // Collect all pagination keys from current form data
      const paginationKeys = new Set<string>();
      currentFormData.forEach((item) => {
        if (item.categoryTypeKey && item.categoryTypeKey !== "custom") {
          paginationKeys.add(item.categoryTypeKey);
        }
        if (item.paginationTypeKey && item.paginationTypeKey !== "custom") {
          paginationKeys.add(item.paginationTypeKey);
        }
        if (item.paramKey) {
          paginationKeys.add(item.paramKey);
        }
        if (item.categoryTypeKey === "custom" && item.categoryTypeCustom) {
          paginationKeys.add(item.categoryTypeCustom);
        }
        if (item.paginationTypeKey === "custom" && item.paginationTypeCustom) {
          paginationKeys.add(item.paginationTypeCustom);
        }
      });

      // Helper to check if key is potentially a pagination key
      const isPaginationKey = (key: string): boolean => {
        if (!key || paginationKeys.has(key)) return paginationKeys.has(key);
        // Check partial matches
        for (const pKey of paginationKeys) {
          if (pKey.startsWith(key) || key.startsWith(pKey)) return true;
        }
        return false;
      };

      // Separate pagination and query params
      const paginationParams = new Map<string, string>();
      const queryParams: Array<{
        enabled: boolean;
        key: string;
        value: string;
      }> = [];

      params.forEach((value, key) => {
        const decodedKey = decodeURIComponent(key).trim();
        const decodedValue = decodeURIComponent(value).trim();

        // Skip completely empty keys (phantom params from malformed URLs)
        if (!decodedKey) return;

        if (isPaginationKey(decodedKey)) {
          paginationParams.set(decodedKey, decodedValue);
        } else {
          // Only add non-pagination params as query params
          // This prevents phantom rows from partial deletions
          queryParams.push({
            enabled: true,
            key: decodedKey,
            value: decodedValue,
          });
        }
      });

      return {
        paginationParams,
        queryParams:
          queryParams.length > 0
            ? queryParams
            : [{ enabled: true, key: "", value: "" }],
        paginationKeys,
      };
    },
    [],
  );

  /**
   * Update form data based on parsed URL params
   * Clean, testable function with single responsibility
   */
  const updateFormDataFromUrl = React.useCallback(
    (
      currentFormData: PaginationParamRow[],
      paginationParams: Map<string, string>,
    ): PaginationParamRow[] => {
      return currentFormData.map((item) => {
        // Handle cursor pagination
        if (paginationType === "cursor") {
          const limitKey =
            item.limitKey === "custom" ? item.limitCustomKey : item.limitKey;
          return {
            ...item,
            limitValue:
              limitKey && paginationParams.has(limitKey)
                ? paginationParams.get(limitKey)!
                : item.limitValue || "",
            cursorValue:
              item.cursorKey && paginationParams.has(item.cursorKey)
                ? paginationParams.get(item.cursorKey)!
                : item.cursorValue || "",
          };
        }

        // Handle token pagination
        if (paginationType === "token") {
          const limitKey =
            item.limitKey === "custom" ? item.limitCustomKey : item.limitKey;
          return {
            ...item,
            limitValue:
              limitKey && paginationParams.has(limitKey)
                ? paginationParams.get(limitKey)!
                : item.limitValue || "",
            tokenValue:
              item.tokenKey && paginationParams.has(item.tokenKey)
                ? paginationParams.get(item.tokenKey)!
                : item.tokenValue || "",
          };
        }

        // Handle next_url pagination
        if (paginationType === "next_url") {
          const limitKey =
            item.limitKey === "custom" ? item.limitCustomKey : item.limitKey;
          return {
            ...item,
            limitValue:
              limitKey && paginationParams.has(limitKey)
                ? paginationParams.get(limitKey)!
                : item.limitValue || "",
            nextUrlValue:
              item.nextUrlKey && paginationParams.has(item.nextUrlKey)
                ? paginationParams.get(item.nextUrlKey)!
                : item.nextUrlValue || "",
          };
        }

        // Handle offset_limit and page_page_size pagination
        const effectiveCategoryKey =
          item.categoryTypeKey === "custom"
            ? item.categoryTypeCustom
            : item.categoryTypeKey;

        const effectivePaginationKey =
          item.paginationTypeKey === "custom"
            ? item.paginationTypeCustom
            : item.paginationTypeKey;

        return {
          ...item,
          categoryTypeValue:
            effectiveCategoryKey && paginationParams.has(effectiveCategoryKey)
              ? paginationParams.get(effectiveCategoryKey)!
              : item.categoryTypeValue || "",
          paginationTypeValue:
            effectivePaginationKey &&
            paginationParams.has(effectivePaginationKey)
              ? paginationParams.get(effectivePaginationKey)!
              : item.paginationTypeValue || "",
          paramValue:
            item.paramKey && paginationParams.has(item.paramKey)
              ? paginationParams.get(item.paramKey)!
              : item.paramValue || "",
        };
      });
    },
    [paginationType],
  );
  // Handler for URL changes - Clean, maintainable approach
  const handleUrlChange = (newUrl: string) => {
    // Always update targetUrl immediately for responsive typing
    setTargetUrl(newUrl);

    // Skip parsing if URL change is coming from pagination block
    if (isUpdatingFromPagination.current) return;

    // Skip parsing if user is typing in pagination fields
    if (isTyping.current) return;

    // Set flag to prevent circular updates
    isUpdatingFromUrl.current = true;

    const trimmedUrl = newUrl.trim();

    // Parse URL into structured data
    const parsed = parseUrlQueryString(trimmedUrl, paginationFormData);

    // Update states based on parsed data
    if (trimmedUrl.includes("?")) {
      const updatedFormData = updateFormDataFromUrl(
        paginationFormData,
        parsed.paginationParams,
      );
      setPaginationFormData(updatedFormData);
      setQueryParams(parsed.queryParams);
    } else {
      // No query string - reset only values, keep structure
      setQueryParams([{ enabled: true, key: "", value: "" }]);
      setPaginationFormData((prev) =>
        prev.map((item) => ({
          ...item,
          categoryTypeValue: "",
          paginationTypeValue: "",
          paramValue: "",
          limitValue: "",
          cursorValue: "",
          tokenValue: "",
          nextUrlValue: "",
        })),
      );
    }

    // Clear the circular update flag
    setTimeout(() => {
      isUpdatingFromUrl.current = false;
    }, 50);

    // Update finalRequestData with loop detection
    if (!requestId) return;

    const isLooping = /\[\*\]/.test(trimmedUrl);
    const loopMatch = trimmedUrl.match(/{{\s*(\w+)\[\*\]\.(\w+)\s*}}/);
    const loopSourcePath = loopMatch?.[1] ?? null;
    const loopVarName = loopMatch?.[2] ?? null;

    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: {
          showAuth: showAuth,
          url: trimmedUrl,
          originalUrl: trimmedUrl,
          isLoop: isLooping,
          loopVariable: isLooping ? loopVarName : null,
          loopSourcePath: isLooping ? loopSourcePath : null,
          loopSourceRequest: isLooping ? requestId : null,
          loopSourceRequestBundle:
            isLooping && selectedLogicBlockId != null
              ? String(selectedLogicBlockId)
              : null,
        },
      }),
    );
  };

  // Handler for method change
  const handleMethodChange = (newMethod: string) => {
    setMethod(newMethod);

    // Clear body content for methods that don't support it
    const methodsWithoutBody = ["GET", "HEAD"];
    const shouldClearBody = methodsWithoutBody.includes(
      newMethod.toUpperCase(),
    );

    if (shouldClearBody) {
      setBody("");
      setBodyType("none");
      setFormData([{ key: "", value: "", enabled: true }]);
    }

    if (!requestId) return;
    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: {
          method: newMethod,
          ...(shouldClearBody && {
            body: "",
            bodyType: "none",
            formData: [{ key: "", value: "", enabled: true }],
          }),
        },
      }),
    );
  };

  // Handler for body change
  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    const newBody = textarea.value;

    // Auto-resize
    textarea.style.height = "50px";
    textarea.style.height = `${textarea.scrollHeight}px`;

    // Validate body content if bodyType is raw
    if (bodyType === "raw") {
      const validation = validateBodyContent(newBody, rawBodyType);

      // Additional validation for JSON with array/object variables
      if (rawBodyType === "json" && validation.isValid) {
        const varUsageValidation = validateVariableUsageInJSON(
          newBody,
          definedVariables,
        );
        setBodyValidationError(varUsageValidation.error);
      } else {
        setBodyValidationError(validation.error);
      }
    } else {
      setBodyValidationError(null);
    }

    // Update local state
    setBody(newBody);

    // Update request data if requestId exists
    if (!requestId) return;

    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: { body: newBody, bodyType, rawBodyType, formData },
      }),
    );
  };

  // Handler for body type change
  const handleBodyTypeChange = (newType: string) => {
    setBodyType(newType);

    // Clear validation error when switching away from raw
    if (newType !== "raw") {
      setBodyValidationError(null);
    } else if (body) {
      // Re-validate if switching to raw and there's already body content
      const validation = validateBodyContent(body, rawBodyType);
      setBodyValidationError(validation.error);
    }

    // Ensure at least one row when switching to form-urlencoded
    if (newType === "form-urlencoded" && formData.length === 0) {
      setFormData([{ key: "", value: "", enabled: true }]);
    }

    if (!requestId) return;
    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: {
          bodyType: newType,
          rawBodyType,
          formData:
            newType === "form-urlencoded" && formData.length === 0
              ? [{ key: "", value: "", enabled: true }]
              : formData,
        },
      }),
    );
  };

  // Handler for raw body type change
  const handleRawBodyTypeChange = (newType: string) => {
    setRawBodyType(newType);

    // Validate existing body content with new type
    if (body && bodyType === "raw") {
      const validation = validateBodyContent(body, newType);
      setBodyValidationError(validation.error);
    }

    if (!requestId) return;
    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: { bodyType, rawBodyType: newType, formData },
      }),
    );
  };

  // Handler for form data change
  const handleFormDataChange = (
    index: number,
    field: "key" | "value" | "enabled",
    value: string | boolean,
  ) => {
    const updated = [...formData];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(updated);
    if (!requestId) return;
    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: { bodyType, rawBodyType, formData: updated },
      }),
    );
  };

  // Handler to add form data row
  const addFormDataRow = () => {
    setFormData([...formData, { key: "", value: "", enabled: true }]);
  };

  // Handler to remove form data row
  const removeFormDataRow = (index: number) => {
    const updated = formData.filter((_, i) => i !== index);
    setFormData(updated);
    if (!requestId) return;
    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: { bodyType, rawBodyType, formData: updated },
      }),
    );
  };

  // Handler for headers change
  const handleHeaderChange = (
    index: number,
    field: "key" | "value",
    value: string,
  ) => {
    const updated = [...headers];
    updated[index][field] = value;
    setHeaders(updated);
    if (!requestId) return;
    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: { headers: updated },
      }),
    );
  };

  // Handler for authToken change
  const handleAuthTokenChange = (newToken: string) => {
    setAuthToken(newToken);
    if (!requestId) return;

    isTogglingAuth.current = true;
    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: {
          auth: {
            type: mapAuthTypeToBackend(authHeader),
            token: newToken,
            username: username,
            password: password,
          },
          showAuth: showAuth,
          headers: headers,
        },
      }),
    );
  };

  // Handler for authHeader change
  const handleAuthHeaderChange = (newAuthHeader: string) => {
    setAuthHeader(newAuthHeader);
    if (!requestId) return;

    isTogglingAuth.current = true;
    //  Preserve ALL auth values when switching types
    if (newAuthHeader === "basic") {
      setShowAuth(true);
      setFinalRequestData((prev: any) =>
        updateRequestInFinalData({
          prev,
          requestId,
          updates: {
            auth: {
              type: mapAuthTypeToBackend("basic"),
              username: username,
              password: password,
              token: authToken,
            },
            showAuth: true,
            headers: headers,
          },
        }),
      );
    } else {
      setShowAuth(true);
      setFinalRequestData((prev: any) =>
        updateRequestInFinalData({
          prev,
          requestId,
          updates: {
            auth: {
              type: mapAuthTypeToBackend(newAuthHeader),
              token: authToken,
              username: username,
              password: password,
            },
            showAuth: true,
            headers: headers,
          },
        }),
      );
    }
  };

  // Handler for username change
  const handleUsernameChange = (newUsername: string) => {
    setUsername(newUsername);
    if (!requestId) return;
    isTogglingAuth.current = true;
    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: {
          auth: {
            type: mapAuthTypeToBackend("basic"),
            username: newUsername,
            password: password,
            token: authToken,
          },
          headers: headers,
        },
      }),
    );
  };

  // Handler for password change
  const handlePasswordChange = (newPassword: string) => {
    setPassword(newPassword);
    if (!requestId) return;
    isTogglingAuth.current = true;
    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: {
          auth: {
            type: mapAuthTypeToBackend("basic"),
            username: username,
            password: newPassword,
            token: authToken,
          },
          headers: headers,
        },
      }),
    );
  };

  // Handler for showAuth toggle
  const handleShowAuthToggle = () => {
    setShowAuth((prev) => {
      const newShowAuth = !prev;
      if (requestId) {
        isTogglingAuth.current = true; // Set flag to skip re-initialization
        setFinalRequestData((prevData: any) =>
          updateRequestInFinalData({
            prev: prevData,
            requestId,
            updates: {
              showAuth: newShowAuth,
              headers: headers, // Preserve current headers state
            },
          }),
        );
      }
      return newShowAuth;
    });
  };

  // Handler for SSL verify toggle
  const handleSslVerifyChange = (checked: boolean) => {
    setSslVerify(checked);

    if (!requestId) return;

    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: { ssl_verify: checked, showAuth: showAuth },
      }),
    );
  };

  // Show settings if sslVerify is false (sslVerify is true initially)
  React.useEffect(() => {
    if (sslVerify === false) {
      setShowSettings(true);
    } else {
      setShowSettings(false);
    }
  }, [sslVerify]);

  // Clean URL when params section is hidden
  React.useEffect(() => {
    if (!showParams && targetUrl.includes("?")) {
      const baseUrl = targetUrl.split("?")[0];
      setTargetUrl(baseUrl);

      // Also update finalRequestData
      if (requestId) {
        setFinalRequestData((prev: any) =>
          updateRequestInFinalData({
            prev,
            requestId,
            updates: {
              url: baseUrl,
              originalUrl: baseUrl,
            },
          }),
        );
      }
    }
  }, [showParams]);

  // Track previous filteration values to prevent unnecessary updates
  const prevFilterationRef = React.useRef<{
    filterationType: string;
    startKey: string;
    endKey: string;
    customDateFormat: string;
    logCollectionPeriod: string;
  }>({
    filterationType: "",
    startKey: "",
    endKey: "",
    customDateFormat: "",
    logCollectionPeriod: "",
  });

  // Update finalRequestData when filteration fields change
  React.useEffect(() => {
    if (!requestId) return;

    // Check if values actually changed to prevent infinite loops
    const hasChanged =
      prevFilterationRef.current.filterationType !== filterationType ||
      prevFilterationRef.current.startKey !== startKey ||
      prevFilterationRef.current.endKey !== endKey ||
      prevFilterationRef.current.customDateFormat !== customDateFormat ||
      prevFilterationRef.current.logCollectionPeriod !== logCollectionPeriod;

    if (!hasChanged) return;

    // Update ref to track current values
    prevFilterationRef.current = {
      filterationType,
      startKey,
      endKey,
      customDateFormat,
      logCollectionPeriod,
    };

    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: {
          filteration:
            filterationType && filterationType !== "none"
              ? {
                  type:
                    filterationType === "custom"
                      ? "custom_date_format"
                      : filterationType,
                  parameter: null,
                  default_cron_start_time: logCollectionPeriod || "none",
                  start_key: startKey || "",
                  end_key: endKey || "",
                  custom_date_format: customDateFormat || "",
                }
              : {
                  type: "none",
                  parameter: null,
                  default_cron_start_time: "none",
                },
        },
      }),
    );
  }, [
    filterationType,
    startKey,
    endKey,
    customDateFormat,
    logCollectionPeriod,
    requestId,
  ]);

  // Auto-show params section on mount if there's filtration or pagination data
  React.useEffect(() => {
    if (!requestId) return;

    // Only auto-show if user hasn't manually toggled
    if (hasUserToggledParams.current) return;

    // Skip if we've already auto-shown params for this request
    if (hasAutoShownParams.current.has(requestId)) return;

    const existingRequest = finalRequestData?.request?.find(
      (req) => req.requestId === requestId,
    );

    if (existingRequest) {
      const hasPagination =
        existingRequest.paginationType &&
        existingRequest.paginationType !== "" &&
        existingRequest.paginationType !== "none";

      const hasFilteration =
        existingRequest.filteration &&
        existingRequest.filteration.type !== "none";

      const hasQueryParams =
        existingRequest.queryParams &&
        Array.isArray(existingRequest.queryParams) &&
        existingRequest.queryParams.some((p: any) => p.key || p.value);

      // Check for params.parameter from backend
      const hasParamsParameter =
        existingRequest.params?.type === "params" &&
        existingRequest.params?.parameter &&
        Array.isArray(existingRequest.params.parameter) &&
        existingRequest.params.parameter.some((p: any) => p.key || p.value);

      // Auto-show params if there's any data
      if (
        hasPagination ||
        hasFilteration ||
        hasQueryParams ||
        hasParamsParameter
      ) {
        setShowParams(true);
        hasAutoShownParams.current.add(requestId);
      } else {
        // Mark as processed even if we don't show params
        hasAutoShownParams.current.add(requestId);
      }
    }
  }, [requestId]);

  // Initialize form fields if requestId changes
  React.useEffect(() => {
    if (!requestId) return;

    // Skip re-initialization if we're just toggling auth
    if (isTogglingAuth.current) {
      isTogglingAuth.current = false;
      return;
    }

    const existingRequest = finalRequestData?.request?.find(
      (req) => req.requestId === requestId,
    );

    if (existingRequest) {
      setBodyType(existingRequest.bodyType || "raw");
      setRawBodyType(existingRequest.rawBodyType || "json");
      setFormData(
        existingRequest.formData || [{ key: "", value: "", enabled: true }],
      );
      setTargetUrl(existingRequest.url || "");
      setMethod(existingRequest.method || "GET");
      setBody(existingRequest.body || "");
      const cleanedHeaders = Array.isArray(existingRequest.headers)
        ? existingRequest.headers.filter((h: any) => h.key || h.value)
        : [];
      setHeaders(cleanedHeaders);
      setSslVerify(
        typeof existingRequest.ssl_verify === "boolean"
          ? existingRequest.ssl_verify
          : true,
      );

      //  Restore auth type
      const savedAuthType = existingRequest.auth?.type;

      //  Map backend type to frontend type
      let frontendAuthType: string;

      if (!savedAuthType || savedAuthType === "") {
        frontendAuthType = "none";
      } else {
        // Map from backend format to frontend format
        switch (savedAuthType) {
          case "basic_auth":
            frontendAuthType = "basic";
            break;
          case "bearer_token":
            frontendAuthType = "bearer";
            break;
          case "jwt_token":
            frontendAuthType = "jwtBearer";
            break;
          case "api_key":
            frontendAuthType = "api-key";
            break;
          case "authorization":
            frontendAuthType = "authorization";
            break;
          default:
            // If it's already in frontend format, use as-is
            frontendAuthType = savedAuthType;
        }
      }

      setAuthHeader(frontendAuthType);

      //  Always restore ALL auth values
      const savedUsername = existingRequest.auth?.username || "";
      const savedPassword = existingRequest.auth?.password || "";
      const savedToken = existingRequest.auth?.token || "";

      setUsername(savedUsername);
      setPassword(savedPassword);
      setAuthToken(savedToken);

      //  Determine if auth should be shown
      const hasBasicAuth = !!(savedUsername || savedPassword);
      const hasTokenAuth = !!savedToken;

      // Set showAuth based on auth type and values
      let calculatedShowAuth2 = false;
      if (frontendAuthType === "basic") {
        calculatedShowAuth2 = hasBasicAuth;
      } else if (
        frontendAuthType !== "none" &&
        frontendAuthType !== "authorization"
      ) {
        calculatedShowAuth2 = hasTokenAuth;
      } else if (frontendAuthType === "authorization") {
        calculatedShowAuth2 = hasTokenAuth;
      } else {
        calculatedShowAuth2 = false;
      }

      setShowAuth(calculatedShowAuth2);

      // Override with explicit showAuth if present (only from backend data, not our saved state)
      if (typeof existingRequest.showAuth === "boolean") {
        setShowAuth(existingRequest.showAuth);
      }

      // Set response if available
      if (finalRequestData?.responses?.[requestId]) {
        setResponse?.(finalRequestData.responses[requestId]);
        setIsResponse?.(true);
      } else {
        setResponse?.(null);
        setIsResponse?.(false);
      }
    } else {
      //  Reset all fields for new request
      setTargetUrl("");
      setMethod("GET");
      setBody("");
      setBodyType("none");
      setRawBodyType("text");
      setFormData([{ key: "", value: "", enabled: true }]);
      setHeaders([]);
      setAuthHeader("authorization");
      setAuthToken("");
      setUsername("");
      setPassword("");
      setShowAuth(false);
      setSslVerify(true);
      setFilterationType("none");
      setStartKey("");
      setEndKey("");
      setCustomDateFormat("");
      setLogCollectionPeriod("none");
      setShowParams(false);
      setResponse?.(null);
      setIsResponse?.(false);
    }
  }, [requestId, finalRequestData]);

  const removeFormDataRowPagination = (index: number) => {
    setPaginationFormData((prev) => {
      if (prev.length > 1) {
        return prev.filter((_, i) => i !== index);
      } else {
        // Last item - clear it and hide pagination section
        setPaginationType("none");
        return [
          {
            paginationEnable: false,
            categoryTypeKey: "",
            categoryTypeValue: "",
            paginationTypeKey: "",
            paginationTypeValue: "",
            paramKey: "",
            paramValue: "",
            categoryTypeCustom: "",
            paginationTypeCustom: "",
            limitKey: "",
            limitValue: "",
            limitCustomKey: "",
            cursorKey: "",
            cursorValue: "",
            cursorSourcePath: "",
            tokenKey: "",
            tokenValue: "",
            tokenSourcePath: "",
            nextUrlKey: "",
            nextUrlValue: "",
            nextUrlSourcePath: "",
          },
        ];
      }
    });
  };

  const addFormDataRowPagination = () => {
    setPaginationFormData((prev) => [
      ...prev,
      {
        paginationEnable: true,
        categoryTypeKey: "",
        categoryTypeValue: "",
        paginationTypeKey: "",
        paginationTypeValue: "",
        paramKey: "",
        paramValue: "",
        categoryTypeCustom: "",
        paginationTypeCustom: "",
        limitKey: "",
        limitValue: "",
        limitCustomKey: "",
        cursorKey: "",
        cursorValue: "",
        cursorSourcePath: "",
        tokenKey: "",
        tokenValue: "",
        tokenSourcePath: "",
        nextUrlKey: "",
        nextUrlValue: "",
        nextUrlSourcePath: "",
      },
    ]);
  };

  const addQueryParamRow = () => {
    setQueryParams((prev) => [...prev, { enabled: true, key: "", value: "" }]);
  };

  const removeQueryParamRow = (index: number) => {
    setQueryParams((prev) => {
      if (prev.length > 1) {
        return prev.filter((_, i) => i !== index);
      } else {
        // Last item - clear it and hide the section
        setShowQueryParams(false);
        return [{ enabled: false, key: "", value: "" }];
      }
    });
  };

  const handleQueryParamChange = (
    index: number,
    field: "enabled" | "key" | "value",
    value: boolean | string,
  ) => {
    setQueryParams((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  const clearQueryParams = () => {
    setQueryParams([{ enabled: true, key: "", value: "" }]);
  };

  // Update finalRequestData when queryParams change
  React.useEffect(() => {
    if (!requestId) return;

    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: {
          queryParams: safeQueryParams,
        },
      }),
    );
  }, [JSON.stringify(safeQueryParams), requestId]);

  // Separate: Load pagination data from finalRequestData ONLY when requestId changes
  React.useEffect(() => {
    if (!requestId) return;

    // Wait for finalRequestData to be available
    if (!finalRequestData?.request) return;

    // Only process once per requestId - but reset when requestId actually changes
    if (lastLoadedRequestId.current === requestId) {
      // Already loaded this request, skip
      return;
    }

    const existingRequest = finalRequestData?.request?.find(
      (req) => req.requestId === requestId,
    );
    if (existingRequest) {
      // Load pagination data - check both frontend format and backend format
      let loadedPaginationType = "";
      let loadedPaginationFormData: PaginationParamRow[] = [];

      // First try frontend format (paginationType field)
      if (existingRequest.paginationType) {
        // Map to proper frontend format - consistent with Init effect mapping
        const rawType = existingRequest.paginationType;
        if (
          rawType === "offset_limit_pagination" ||
          rawType === "offset_limit"
        ) {
          loadedPaginationType = "offset_limit";
        } else if (
          rawType === "page_size_pagination" ||
          rawType === "page_size"
        ) {
          loadedPaginationType = "page_page_size";
        } else if (rawType === "cursor_pagination" || rawType === "cursor") {
          loadedPaginationType = "cursor";
        } else if (rawType === "token_pagination" || rawType === "token") {
          loadedPaginationType = "token";
        } else if (
          rawType === "next_url_pagination" ||
          rawType === "next_url"
        ) {
          loadedPaginationType = "next_url";
        } else {
          // Fallback: just remove _pagination suffix
          loadedPaginationType = rawType.replace(/_pagination$/, "");
        }

        if (
          existingRequest.paginationFormData &&
          Array.isArray(existingRequest.paginationFormData) &&
          existingRequest.paginationFormData.length > 0
        ) {
          // Normalize the frontend data to ensure custom keys are properly structured
          const offsetKeyStandard = ["offset", "skip", "start"];
          const limitKeyStandard = ["limit", "count", "pageSize", "perPage"];
          const pageKeyStandard = ["page", "p", "pageNumber"];
          const perPageKeyStandard = ["size", "pageSize", "perPage"];

          loadedPaginationFormData = existingRequest.paginationFormData.map(
            (row: PaginationParamRow) => {
              const normalizedRow = { ...row };

              // Normalize offset_limit pagination
              if (loadedPaginationType === "offset_limit") {
                // Check categoryTypeKey (offset field)
                if (
                  normalizedRow.categoryTypeKey &&
                  normalizedRow.categoryTypeKey !== "custom" &&
                  !offsetKeyStandard.includes(normalizedRow.categoryTypeKey)
                ) {
                  normalizedRow.categoryTypeCustom =
                    normalizedRow.categoryTypeKey;
                  normalizedRow.categoryTypeKey = "custom";
                }
                // Check paginationTypeKey (limit field)
                if (
                  normalizedRow.paginationTypeKey &&
                  normalizedRow.paginationTypeKey !== "custom" &&
                  !limitKeyStandard.includes(normalizedRow.paginationTypeKey)
                ) {
                  normalizedRow.paginationTypeCustom =
                    normalizedRow.paginationTypeKey;
                  normalizedRow.paginationTypeKey = "custom";
                }
              }

              // Normalize page_page_size pagination
              if (loadedPaginationType === "page_page_size") {
                // Check categoryTypeKey (page field)
                if (
                  normalizedRow.categoryTypeKey &&
                  normalizedRow.categoryTypeKey !== "custom" &&
                  !pageKeyStandard.includes(normalizedRow.categoryTypeKey)
                ) {
                  normalizedRow.categoryTypeCustom =
                    normalizedRow.categoryTypeKey;
                  normalizedRow.categoryTypeKey = "custom";
                }
                // Check paginationTypeKey (per_page field)
                if (
                  normalizedRow.paginationTypeKey &&
                  normalizedRow.paginationTypeKey !== "custom" &&
                  !perPageKeyStandard.includes(normalizedRow.paginationTypeKey)
                ) {
                  normalizedRow.paginationTypeCustom =
                    normalizedRow.paginationTypeKey;
                  normalizedRow.paginationTypeKey = "custom";
                }
              }

              // Normalize cursor, token, next_url pagination
              if (
                loadedPaginationType === "cursor" ||
                loadedPaginationType === "token" ||
                loadedPaginationType === "next_url"
              ) {
                // Check limitKey - only "limit" is standard
                if (
                  normalizedRow.limitKey &&
                  normalizedRow.limitKey !== "custom" &&
                  normalizedRow.limitKey !== "limit"
                ) {
                  normalizedRow.limitCustomKey = normalizedRow.limitKey;
                  normalizedRow.limitKey = "custom";
                }
              }

              // Auto-fill source path from value if empty (handles migrated/legacy data)
              if (
                loadedPaginationType === "token" &&
                !normalizedRow.tokenSourcePath &&
                normalizedRow.tokenValue
              ) {
                normalizedRow.tokenSourcePath = normalizedRow.tokenValue;
              }
              // next_url doesn't need source path - the complete URL comes from API
              if (
                loadedPaginationType === "cursor" &&
                !normalizedRow.cursorSourcePath &&
                normalizedRow.cursorValue
              ) {
                normalizedRow.cursorSourcePath = normalizedRow.cursorValue;
              }

              return normalizedRow;
            },
          );
        }
      }
      // If not in frontend format, try backend format (pagination object)
      else if (
        existingRequest.pagination &&
        existingRequest.pagination.type &&
        existingRequest.pagination.type !== "none"
      ) {
        const backendType = existingRequest.pagination.type;
        const param = existingRequest.pagination.parameter || {};

        // Map backend type to frontend type
        if (backendType === "offset_limit_pagination")
          loadedPaginationType = "offset_limit";
        else if (backendType === "page_size_pagination")
          loadedPaginationType = "page_page_size";
        else if (backendType === "cursor_pagination")
          loadedPaginationType = "cursor";
        else if (backendType === "token_pagination")
          loadedPaginationType = "token";
        else if (backendType === "next_url_pagination")
          loadedPaginationType = "next_url";

        // Parse backend pagination parameter object into paginationFormData
        if (loadedPaginationType && Object.keys(param).length > 0) {
          const row: PaginationParamRow = {
            paginationEnable: true,
            categoryTypeKey: "",
            categoryTypeValue: "",
            paginationTypeKey: "",
            paginationTypeValue: "",
            paramKey: "",
            paramValue: "",
            categoryTypeCustom: "",
            paginationTypeCustom: "",
            limitKey: "",
            limitValue: "",
            limitCustomKey: "",
            cursorKey: "",
            cursorValue: "",
            cursorSourcePath: "",
            tokenKey: "",
            tokenValue: "",
            tokenSourcePath: "",
            nextUrlKey: "",
            nextUrlValue: "",
            nextUrlSourcePath: "",
          };

          if (loadedPaginationType === "offset_limit") {
            const offsetKeyStandard = ["offset", "skip", "start"];
            const limitKeyStandard = ["limit", "count", "pageSize", "perPage"];

            const offsetKey = param.offset_key || "";
            const limitKey = param.limit_key || "";

            // Handle offset key
            if (offsetKey && !offsetKeyStandard.includes(offsetKey)) {
              row.categoryTypeKey = "custom";
              row.categoryTypeCustom = offsetKey;
            } else {
              row.categoryTypeKey = offsetKey;
            }
            row.categoryTypeValue = param.offset_value || "";

            // Handle limit key
            if (limitKey && !limitKeyStandard.includes(limitKey)) {
              row.paginationTypeKey = "custom";
              row.paginationTypeCustom = limitKey;
            } else {
              row.paginationTypeKey = limitKey;
            }
            row.paginationTypeValue = param.limit_value || "";
          } else if (loadedPaginationType === "page_page_size") {
            const pageKeyStandard = ["page", "p", "pageNumber"];
            const perPageKeyStandard = ["size", "pageSize", "perPage"];

            const pageKey = param.page_key || "";
            const perPageKey = param.per_page_key || "";

            // Handle page key
            if (pageKey && !pageKeyStandard.includes(pageKey)) {
              row.categoryTypeKey = "custom";
              row.categoryTypeCustom = pageKey;
            } else {
              row.categoryTypeKey = pageKey;
            }
            row.categoryTypeValue = param.page_value || "";

            // Handle per page key
            if (perPageKey && !perPageKeyStandard.includes(perPageKey)) {
              row.paginationTypeKey = "custom";
              row.paginationTypeCustom = perPageKey;
            } else {
              row.paginationTypeKey = perPageKey;
            }
            row.paginationTypeValue = param.per_page_value || "";
          } else if (loadedPaginationType === "cursor") {
            const limitKeyValue = param.limit_key || "";

            // Handle limit key - only "limit" is standard
            if (limitKeyValue && limitKeyValue !== "limit") {
              row.limitKey = "custom";
              row.limitCustomKey = limitKeyValue;
            } else {
              row.limitKey = limitKeyValue;
            }
            row.limitValue = param.limit_value || "";
            row.cursorKey = param.cursor_key || "";
            row.cursorValue = param.cursor_value || "";
            row.cursorSourcePath = param.cursor_value || "";
          } else if (loadedPaginationType === "token") {
            const limitKeyValue = param.limit_key || "";

            // Handle limit key - only "limit" is standard
            if (limitKeyValue && limitKeyValue !== "limit") {
              row.limitKey = "custom";
              row.limitCustomKey = limitKeyValue;
            } else {
              row.limitKey = limitKeyValue;
            }
            row.limitValue = param.limit_value || "";
            row.tokenKey = param.token_key || "";
            row.tokenValue = param.token_value || "";
            row.tokenSourcePath = param.token_value || "";
          } else if (loadedPaginationType === "next_url") {
            const limitKeyValue = param.limit_key || "";

            // Handle limit key - only "limit" is standard
            if (limitKeyValue && limitKeyValue !== "limit") {
              row.limitKey = "custom";
              row.limitCustomKey = limitKeyValue;
            } else {
              row.limitKey = limitKeyValue;
            }
            row.limitValue = param.limit_value || "";
            row.nextUrlKey = param.next_url_key || "";
            row.nextUrlValue = "";
            row.nextUrlSourcePath = ""; // Response path not needed for next_url
          }

          loadedPaginationFormData = [row];
        }
      }

      // Set pagination type and form data
      setPaginationType(loadedPaginationType);

      if (loadedPaginationFormData.length > 0) {
        setPaginationFormData(loadedPaginationFormData);
      } else {
        setPaginationFormData([
          {
            paginationEnable: true,
            categoryTypeKey: "",
            categoryTypeValue: "",
            paginationTypeKey: "",
            paginationTypeValue: "",
            paramKey: "",
            paramValue: "",
            categoryTypeCustom: "",
            paginationTypeCustom: "",
            limitKey: "",
            limitValue: "",
            limitCustomKey: "",
            cursorKey: "",
            cursorValue: "",
            cursorSourcePath: "",
            tokenKey: "",
            tokenValue: "",
            tokenSourcePath: "",
            nextUrlKey: "",
            nextUrlValue: "",
            nextUrlSourcePath: "",
          },
        ]);
      }

      // Load query params with fallback to backend params.parameter.
      // This prevents losing prefilled params when queryParams is absent.
      let loadedQueryParams: Array<{
        enabled: boolean;
        key: string;
        value: string;
      }> = [];

      // Build set of pagination keys to exclude from query params list
      const paginationKeys = new Set<string>();

      if (
        loadedPaginationType &&
        loadedPaginationType !== "none" &&
        loadedPaginationFormData.length > 0
      ) {
        loadedPaginationFormData.forEach((row: any) => {
          if (!row.paginationEnable) return;

          if (loadedPaginationType === "offset_limit") {
            if (row.categoryTypeKey && row.categoryTypeKey !== "custom") {
              paginationKeys.add(row.categoryTypeKey);
            }
            if (row.paginationTypeKey && row.paginationTypeKey !== "custom") {
              paginationKeys.add(row.paginationTypeKey);
            }
            if (row.categoryTypeKey === "custom" && row.categoryTypeCustom) {
              paginationKeys.add(row.categoryTypeCustom);
            }
            if (
              row.paginationTypeKey === "custom" &&
              row.paginationTypeCustom
            ) {
              paginationKeys.add(row.paginationTypeCustom);
            }
          } else if (
            loadedPaginationType === "page_page_size" ||
            loadedPaginationType === "page_size"
          ) {
            if (row.categoryTypeKey && row.categoryTypeKey !== "custom") {
              paginationKeys.add(row.categoryTypeKey);
            }
            if (row.paginationTypeKey && row.paginationTypeKey !== "custom") {
              paginationKeys.add(row.paginationTypeKey);
            }
          } else if (
            ["cursor", "token", "next_url"].includes(loadedPaginationType)
          ) {
            if (row.limitKey && row.limitKey !== "custom") {
              paginationKeys.add(row.limitKey);
            }
            if (row.limitKey === "custom" && row.limitCustomKey) {
              paginationKeys.add(row.limitCustomKey);
            }
            if (loadedPaginationType === "cursor" && row.cursorKey) {
              paginationKeys.add(row.cursorKey);
            }
            if (loadedPaginationType === "token" && row.tokenKey) {
              paginationKeys.add(row.tokenKey);
            }
            if (loadedPaginationType === "next_url" && row.nextUrlKey) {
              paginationKeys.add(row.nextUrlKey);
            }
          }
        });
      }

      if (
        existingRequest.queryParams &&
        Array.isArray(existingRequest.queryParams) &&
        existingRequest.queryParams.length > 0
      ) {
        // Filter out pagination keys from queryParams
        loadedQueryParams = existingRequest.queryParams.filter(
          (p: any) => !paginationKeys.has(p.key),
        );
      }

      // Fallback: backend format stores query params under params.parameter
      if (
        loadedQueryParams.length === 0 &&
        existingRequest.params?.type === "params" &&
        Array.isArray(existingRequest.params?.parameter)
      ) {
        loadedQueryParams = existingRequest.params.parameter
          .filter((p: any) => {
            const key = String(p?.key || "").trim();
            const value = String(p?.value || "").trim();
            return (key || value) && !paginationKeys.has(key);
          })
          .map((p: any) => ({
            enabled: true,
            key: String(p?.key || ""),
            value: String(p?.value || ""),
          }));
      }

      // Set query params (only saved query params, NOT pagination params)
      if (loadedQueryParams.length > 0) {
        setQueryParams(loadedQueryParams);
      } else {
        setQueryParams([{ enabled: true, key: "", value: "" }]);
      }

      // Auto-show Params section if there's any params-related data
      const hasPagination =
        loadedPaginationType &&
        loadedPaginationType !== "" &&
        loadedPaginationType !== "none";

      const hasFilteration =
        existingRequest.filteration &&
        existingRequest.filteration.type !== "none";

      const hasQueryParams =
        loadedQueryParams.length > 0 &&
        loadedQueryParams.some((p: any) => p.key || p.value);

      // Auto-show if there's any data (pagination, filtration, or query params)
      // Only set to true if there's data - don't force false to respect manual toggles
      if (hasPagination || hasFilteration || hasQueryParams) {
        setShowParams(true);
        hasUserToggledParams.current = false; // Reset toggle flag so auto-show works
      }

      // Set pagination loaded flag after a delay to allow URL to be properly set
      // This prevents PaginationBlock's useEffect from overwriting the loaded URL
      setTimeout(() => {
        isPaginationLoaded.current = true;

        // Save the loaded pagination data to finalRequestData in frontend format
        // This ensures that even backend-only format gets converted and saved
        if (loadedPaginationType || loadedPaginationFormData.length > 0) {
          setFinalRequestData((prev: any) =>
            updateRequestInFinalData({
              prev,
              requestId,
              updates: {
                paginationType: loadedPaginationType,
                paginationFormData:
                  loadedPaginationFormData.length > 0
                    ? loadedPaginationFormData
                    : prev?.request?.find((r: any) => r.requestId === requestId)
                        ?.paginationFormData || [],
              },
            }),
          );
        }
      }, 200);
    } else {
      // Reset pagination for new request
      setPaginationType("none");
      setPaginationFormData([
        {
          paginationEnable: true,
          categoryTypeKey: "",
          categoryTypeValue: "",
          paginationTypeKey: "",
          paginationTypeValue: "",
          paramKey: "",
          paramValue: "",
          categoryTypeCustom: "",
          paginationTypeCustom: "",
          limitKey: "",
          limitValue: "",
          limitCustomKey: "",
          cursorKey: "",
          cursorValue: "",
          cursorSourcePath: "",
          tokenKey: "",
          tokenValue: "",
          tokenSourcePath: "",
          nextUrlKey: "",
          nextUrlValue: "",
          nextUrlSourcePath: "",
        },
      ]);
      setQueryParams([{ enabled: true, key: "", value: "" }]);
    }

    // Mark this requestId as loaded
    lastLoadedRequestId.current = requestId;

    // Set pagination loaded flag to allow saving changes
    setTimeout(() => {
      isPaginationLoaded.current = true;
    }, 200);
  }, [requestId, finalRequestData]);

  // Reset pagination loaded state when bundle or logic type changes
  React.useEffect(() => {
    lastLoadedRequestId.current = undefined;
    isPaginationLoaded.current = false;
    hasAutoShownParams.current.clear();
    lastLoadedFilterationRequestId.current = undefined;
  }, [selectedLogicBlockId, selectedLogicName]);

  // Combined: Save all pagination changes to finalRequestData (only after initial load)
  React.useEffect(() => {
    if (!requestId) {
      return;
    }

    if (!isPaginationLoaded.current) {
      return;
    }

    // Skip if update is coming from URL to prevent overwriting
    if (isUpdatingFromUrl.current) {
      return;
    }

    // Normalize pagination type (remove _pagination suffix if present)
    const normalizedType =
      paginationType?.replace(/_pagination$/, "") || "none";

    setFinalRequestData((prev: any) => {
      const updated = updateRequestInFinalData({
        prev,
        requestId,
        updates: {
          paginationType: normalizedType,
          paginationFormData: paginationFormData,
          queryParams: queryParams,
        },
      });

      return updated;
    });
  }, [
    paginationType,
    JSON.stringify(paginationFormData),
    JSON.stringify(queryParams),
    requestId,
  ]);

  const targetUrlRef = React.useRef(targetUrl);

  React.useEffect(() => {
    targetUrlRef.current = targetUrl;
  }, [targetUrl]);

  const handleFormDataChangePagination = (
    index: number,
    field: keyof PaginationParamRow,
    value: boolean | string,
  ) => {
    // Set typing flag to prevent URL sync interference
    // Use longer delay for value fields to prevent URL sync from overwriting
    const isValueField =
      field === "categoryTypeValue" || field === "paginationTypeValue";
    isTyping.current = true;

    setPaginationFormData((prev) => {
      const updated = prev.map((row, i) => {
        if (i !== index) return row;

        const updatedRow = { ...row, [field]: value };

        // Smart Sync: automatically pick up value from URL if custom key matches
        if (
          field === "categoryTypeCustom" ||
          field === "paginationTypeCustom"
        ) {
          // Parse current URL params using the Ref to ensure freshness
          const currentUrl = targetUrlRef.current || "";
          const queryStringMatch = currentUrl.match(/\?(.+)$/);

          if (queryStringMatch) {
            const params = new URLSearchParams(queryStringMatch[1]);
            const newKey = value as string;

            // If the typed key exists in URL, update the value field immediately
            // This prevents the effective empty value from clearing the URL param
            if (newKey && params.has(newKey)) {
              if (field === "categoryTypeCustom") {
                updatedRow.categoryTypeValue = params.get(newKey) || "";
              } else {
                updatedRow.paginationTypeValue = params.get(newKey) || "";
              }
            }
          }
        }

        return updatedRow;
      });

      return updated;
    });

    // Clear typing flag after state update completes
    // Use longer delay for value fields to ensure URL update doesn't override
    setTimeout(
      () => {
        isTyping.current = false;
      },
      isValueField ? 150 : 50,
    );
  };

  const handlePaginationUrlChange = (queryParams: string) => {
    // Skip if we're currently updating from URL to prevent circular updates
    if (isUpdatingFromUrl.current) {
      return;
    }

    // Skip during initial pagination load to preserve the full URL from saved data
    if (!isPaginationLoaded.current) {
      return;
    }

    // Set flag to indicate URL is being updated by pagination block
    isUpdatingFromPagination.current = true;

    // Get base URL without query parameters
    const baseUrl = targetUrl.split("?")[0];

    // Build new URL (empty queryParams clears pagination)
    const newUrl = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;

    // Only update if the URL actually changed
    if (newUrl === targetUrl) {
      isUpdatingFromPagination.current = false;
      return;
    }

    // Update URL
    setTargetUrl(newUrl);

    // Update finalRequestData with loop detection
    if (requestId) {
      // Detect loop pattern
      const isLooping = /\[\*\]/.test(newUrl);
      const loopMatch = newUrl.match(/{{\s*(\w+)\[\*\]\.(\w+)\s*}}/);
      const loopSourcePath = loopMatch?.[1] ?? null;
      const loopVarName = loopMatch?.[2] ?? null;

      setFinalRequestData((prev: any) =>
        updateRequestInFinalData({
          prev,
          requestId,
          updates: {
            showAuth: showAuth,
            url: newUrl,
            originalUrl: newUrl,
            isLoop: isLooping,
            loopVariable: isLooping ? loopVarName : null,
            loopSourcePath: isLooping ? loopSourcePath : null,
            loopSourceRequest: isLooping ? requestId : null,
            loopSourceRequestBundle:
              isLooping && selectedLogicBlockId != null
                ? String(selectedLogicBlockId)
                : null,
          },
        }),
      );
    }

    // Clear the flag after state updates complete
    setTimeout(() => {
      isUpdatingFromPagination.current = false;
    }, 100);
  };

  // Function to clear all auth fields
  const clearAllAuthFields = () => {
    setAuthToken("");
    setUsername("");
    setPassword("");

    if (!requestId) return;

    setFinalRequestData((prev: any) =>
      updateRequestInFinalData({
        prev,
        requestId,
        updates: {
          auth: {
            type: "",
            token: "",
            username: "",
            password: "",
          },
        },
      }),
    );
  };

  // Function to handle Params toggle and clear data when closing
  const handleParamsToggle = () => {
    // Mark that user has manually toggled params
    hasUserToggledParams.current = true;

    // When opening params for the first time with no data, initialize both to "none"
    if (!showParams) {
      // Mark this request as processed for auto-show
      if (requestId) {
        hasAutoShownParams.current.add(requestId);
      }

      // Only set if they're not already set (empty or undefined)
      if (!paginationType) {
        setPaginationType("none");
      }
      if (!filterationType || filterationType === "") {
        setFilterationType("none");
      }
    }

    setShowParams(!showParams);
  };

  return (
    <>
      <div className="text-sm">
        {/* Loader */}
        <PageLoader show={loading || isConfigLoading} />

        {/* open field mapping dialog */}
        {openMapfieldsDialog?.open &&
          openMapfieldsDialog?.requestId === requestId && (
            <FieldMappingDialog
              open={openMapfieldsDialog.open}
              onClose={() => {
                dispatch({
                  type: MeshPluginActions.SET_OPEN_MAPFIELDS_DIALOG,
                  payload: { open: false, requestId: null },
                });
                setSelectedMappingPath(null);
              }}
              responseData={selectedMappingPath?.value ?? null}
              selectedFieldPath={selectedMappingPath?.path ?? null}
              setLogMappedFields={setLogMappedFields}
              setMappingSaveData={setMappingSaveData}
              currentBundleId={selectedLogicBlockId}
              requestId={requestId}
              selectedOption={selectedOption}
              pluginId={pluginId ?? ""}
            />
          )}

        {/* Request Config */}
        <div className="flex items-center gap-1 ">
          <Select value={method} onValueChange={handleMethodChange}>
            <SelectTrigger className="h-10 w-[80px] text-xs text-primaryAmber">
              <SelectValue placeholder="Method" />
            </SelectTrigger>
            <SelectContent>
              {httpMethods.map((m) => (
                <SelectItem key={m} value={m} className="text-xs">
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <input
            type="text"
            placeholder="Target API URL"
            value={targetUrl}
            onChange={(e) => handleUrlChange(e.target.value)}
            className="flex-1 border p-2 rounded bg-background h-10"
            name="targetUrl"
          />
          <Button
            onClick={sendApiRequest}
            disabled={!targetUrl}
            className="text-background h-9 rounded bg-primaryBlue font-medium"
          >
            Send
          </Button>
        </div>

        <div className="flex items-center gap-3 w-full mt-3 border pt-1 pb-1 border-lightGray px-2">
          {/* add header  */}
          <button
            className={`text-primaryBlue flex items-center gap-1 border rounded-md px-3 py-2 ${headers.length > 0 ? "border bg-lightGray " : ""} `}
            onClick={headers.length > 0 ? () => setHeaders([]) : addHeader}
          >
            {headers.length > 0 ? (
              <CircleMinus size={14} />
            ) : (
              <PlusCircle size={14} />
            )}{" "}
            Headers
          </button>

          {/* Toggle Auth Section */}
          <button
            type="button"
            onClick={handleShowAuthToggle}
            className={`text-sm text-primary border px-3 py-2 rounded-md ${showAuth ? "bg-lightGray" : ""}`}
          >
            <span
              className="flex items-center gap-1"
              onClick={clearAllAuthFields}
            >
              {showAuth ? <CircleMinus size={14} /> : <CirclePlus size={14} />}{" "}
              Authorization
            </span>
          </button>

          {/* params toggles */}
          <div>
            <button
              onClick={handleParamsToggle}
              className={`text-sm text-primaryAmber border px-3 py-2 rounded-md ${showParams ? "bg-lightGray" : ""}`}
            >
              {showParams ? (
                <span className="flex items-center gap-1">
                  <CircleMinus size={14} /> Params
                </span>
              ) : (
                "Params"
              )}
            </button>
          </div>

          {/* Toggle SSL Verification */}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`text-sm text-primaryGreen border px-3 py-2 rounded-md ${showSettings ? "bg-lightGray" : ""}`}
          >
            {showSettings ? (
              <span className="flex items-center gap-1">
                <CircleMinus size={14} /> Settings
              </span>
            ) : (
              "Settings"
            )}
          </button>
        </div>

        {/* Headers Section */}
        {headers.length > 0 && (
          <QueryBuilderHeader
            headers={headers}
            handleHeaderChange={handleHeaderChange}
            removeHeader={removeHeader}
            addHeader={addHeader}
          />
        )}

        {/* Authorization Section */}
        {showAuth && (
          <Authorization
            authHeader={authHeader}
            authToken={authToken}
            username={username}
            password={password}
            customAuthKey={customAuthKey}
            setCustomAuthKey={setCustomAuthKey}
            handleAuthHeaderChange={handleAuthHeaderChange}
            handleAuthTokenChange={handleAuthTokenChange}
            handleUsernameChange={handleUsernameChange}
            handlePasswordChange={handlePasswordChange}
          />
        )}

        {showParams && (
          <div className="py-2">
            <PaginationBlock
              visible={showParams}
              showQueryParams={showQueryParams}
              setShowQueryParams={setShowQueryParams}
              formData={paginationFormData}
              onChange={handleFormDataChangePagination}
              onAddRow={addFormDataRowPagination}
              onRemoveRow={removeFormDataRowPagination}
              onUrlChange={handlePaginationUrlChange}
              setFormData={setPaginationFormData}
              queryParams={queryParams}
              onQueryParamChange={handleQueryParamChange}
              onAddQueryParam={addQueryParamRow}
              onRemoveQueryParam={removeQueryParamRow}
              onClearQueryParams={clearQueryParams}
              paginationType={paginationType}
              handlePaginationTypeChange={handlePaginationTypeChange}
              selectedLogicName={selectedLogicName}
              filterationType={filterationType}
              onFilterationChange={handleFilterationChange}
              requestId={requestId}
              savedStartKey={
                finalRequestData?.request?.find(
                  (req) => req.requestId === requestId,
                )?.filteration?.start_key
              }
              savedEndKey={
                finalRequestData?.request?.find(
                  (req) => req.requestId === requestId,
                )?.filteration?.end_key
              }
              savedCustomDateFormat={
                finalRequestData?.request?.find(
                  (req) => req.requestId === requestId,
                )?.filteration?.custom_date_format
              }
              savedLogCollectionPeriod={
                finalRequestData?.request?.find(
                  (req) => req.requestId === requestId,
                )?.filteration?.default_cron_start_time
              }
              savedPaginationType={
                finalRequestData?.request?.find(
                  (req) => req.requestId === requestId,
                )?.paginationType
              }
              savedPaginationFormData={
                finalRequestData?.request?.find(
                  (req) => req.requestId === requestId,
                )?.paginationFormData
              }
              savedQueryParams={
                finalRequestData?.request?.find(
                  (req) => req.requestId === requestId,
                )?.queryParams
              }
            />
          </div>
        )}

        {/* Show settings if enabled */}
        {showSettings && (
          <>
            <p className="font-bold text-md ml-1 text-primaryGreen mt-2">
              Settings
            </p>
            <div className="mt-1 p-2 flex bg-lightGreen rounded-md">
              <div className="flex items-center gap-2 m-2">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={sslVerify}
                  onChange={(e) => handleSslVerifyChange(e.target.checked)}
                />
                <label className="text-xs">SSL Verification</label>
              </div>
            </div>
          </>
        )}

        {/* Body Input */}
        {["POST", "PUT", "PATCH", "DELETE"].includes(method) && (
          // Query Builder Body Component
          <QueryBuilderBody
            bodyState={{
              bodyType,
              rawBodyType,
              body,
              bodyValidationError,
              formData,
            }}
            bodyHandlers={{
              handleBodyTypeChange,
              handleRawBodyTypeChange,
              handleBodyChange,
              handleFormDataChange,
              addFormDataRow,
              removeFormDataRow,
            }}
          />
        )}

        {/* Query builder response */}
        <QueryBuilderResponse response={response} renderJson={renderJson} />

        {/* Key Mapping Dialog component*/}
        {showKeyDialog && (
          <ResponseActionsDialog
            selectedKey={selectedKey}
            handleSelectVariablesOption={handleSelectVariablesOption}
            mappedRequestIds={mappedRequestIds}
            inputName={inputName}
            setInputName={setInputName}
            isAuthVar={isAuthVar}
            setIsAuthVar={setIsAuthVar}
            isLogMapping={isLogMapping}
            handleAddVariableMapping={handleAddVariableMapping}
            handleSetMappingData={handleSetMappingData}
            selectedOption={selectedOption}
            mapRefInput={mapRefInput}
            selectedLogicBlockId={
              selectedLogicBlockId != null ? String(selectedLogicBlockId) : null
            }
            setSelectedKey={setSelectedKey}
            setShowKeyDialog={setShowKeyDialog}
            selectedLogicName={selectedLogicName}
            requestId={requestId}
          />
        )}

        {errorMessage && (
          <div className="mt-4 border border-destructive bg-destructive/10 p-4 rounded-md">
            <div className="flex items-start gap-2">
              <strong className="text-destructive">Error:</strong>
              <div className="flex-1">
                {/* Main error message */}
                <p className="text-destructive break-all whitespace-pre-wrap">
                  {typeof errorMessage === "object" && errorMessage?.message
                    ? errorMessage.message
                    : String(errorMessage)}
                </p>

                {/* Show additional error details if available */}
                {typeof errorMessage === "object" && (
                  <details className="mt-2 text-xs">
                    <summary className="cursor-pointer text-muted-foreground hover:text-primary">
                      View Details
                    </summary>
                    <pre className="mt-2 p-2 bg-background rounded overflow-auto max-h-[200px]">
                      {JSON.stringify(errorMessage, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default QueryBuilder;
